/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * FormBuilderEditor — Enhanced Form Builder Configuration Panel
 * Features: row-based grid layout, stage-based multi-step forms, 12 design presets
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../../theme/ThemeContext';
import FormDataTab, { allFormFields } from './FormDataTab';
import LookupFieldConfig from './LookupFieldConfig';
import { FormBuilderContext } from './formBuilderContext';

// ─── UID helper ───────────────────────────────────────────────────────────────
const uid = (p = 'x') => `${p}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;

// ─── Field type metadata (label, icon, badge color) ──────────────────────────
const TYPE_META = {
  text:     { label: 'Text',     icon: 'bi-fonts',         bg: '#dbeafe', color: '#1d4ed8' },
  email:    { label: 'Email',    icon: 'bi-envelope',      bg: '#e0e7ff', color: '#4338ca' },
  number:   { label: 'Number',   icon: 'bi-hash',          bg: '#fef3c7', color: '#b45309' },
  textarea: { label: 'Textarea', icon: 'bi-text-left',     bg: '#dcfce7', color: '#15803d' },
  select:   { label: 'Dropdown', icon: 'bi-chevron-expand',bg: '#f3e8ff', color: '#7e22ce' },
  checkbox: { label: 'Checkbox', icon: 'bi-check2-square', bg: '#fce7f3', color: '#be185d' },
  radio:    { label: 'Radio',    icon: 'bi-ui-radios',     bg: '#ffedd5', color: '#c2410c' },
  date:     { label: 'Date',     icon: 'bi-calendar3',     bg: '#ccfbf1', color: '#0f766e' },
  file:     { label: 'File',     icon: 'bi-upload',        bg: '#f1f5f9', color: '#475569' },
  password: { label: 'Password', icon: 'bi-key',           bg: '#fee2e2', color: '#b91c1c' },
  lookup:   { label: 'Lookup',   icon: 'bi-table',         bg: '#f3e8ff', color: '#7e22ce' },
  datetime: { label: 'Date & time', icon: 'bi-clock',       bg: '#cffafe', color: '#0e7490' },
  geo_point:{ label: 'Location', icon: 'bi-geo-alt',        bg: '#ffe4e6', color: '#be123c' },
};

const FIELD_TYPES = Object.entries(TYPE_META).map(([value, m]) => ({ value, ...m }));

// ─── Default structures ───────────────────────────────────────────────────────
const newField = (label = 'New Field', type = 'text', extra = {}) => ({
  id: uid('field'), label, type,
  required: false, placeholder: '', helpText: '',
  options: [], isSensitive: false, columnSpan: 1, fullWidth: false, ...extra,
});

const newRow = (fieldsPerRow = 2) => ({
  rowId: uid('row'), fieldsPerRow, fields: [],
  gap: 12, verticalSpacing: 16, alignment: 'stretch',
});

const newStage = (num = 1) => ({
  stageId: uid('stage'), stageNumber: num, stageLabel: `Stage ${num}`,
  stageDescription: '', stageIcon: '', stageIconColor: '#4e2e80',
  rows: [newRow(2)], validationMode: 'on-next',
  allowSkip: false, skipLabel: 'Skip', nextLabel: 'Next', backLabel: 'Back',
  summaryOnComplete: false, autoSaveOnComplete: true,
});

// ─── Default design tokens ────────────────────────────────────────────────────
export const DEFAULT_TOKENS = {
  inputBgColor: '#ffffff',         inputBgColorFocus: '#ffffff',
  inputBorderColor: '#e2e8f0',     inputBorderColorFocus: '#4e2e80',
  inputBorderColorError: '#dc2626',
  inputBorderWidth: 1,             inputBorderStyle: 'solid',   inputBorderRadius: 6,
  inputPaddingX: 12,               inputPaddingY: 8,             inputFontSize: 14,
  inputFontFamily: '',             inputFontWeight: 400,
  inputFontStyle: 'normal',        inputTextAlign: 'left',
  inputTextColor: '#1e293b',       inputPlaceholderColor: '#94a3b8',
  inputPlaceholderFontSize: 0,     inputPlaceholderFontWeight: 0,
  inputPlaceholderFontStyle: 'normal', inputPlaceholderFontFamily: '',
  inputHeight: 38,                 inputShadow: 'none',
  inputShadowFocus: 'ring',        inputFocusRingColor: '#4e2e80',
  inputFocusRingWidth: 2,          inputTransitionDuration: 150,
  labelFontSize: 13,               labelFontWeight: 600,         labelColor: '#374151',
  labelFontFamily: '',             labelFontStyle: 'normal',     labelTextAlign: 'left',
  labelPosition: 'top',            labelMarginBottom: 4,         requiredMarkColor: '#dc2626',
  helpTextFontSize: 11,            helpTextColor: '#64748b',
  errorFontSize: 11,               errorColor: '#dc2626',        errorIconVisible: true,
  successColor: '#16a34a',         successIconVisible: true,
  submitBgColor: '#4e2e80',        submitBgColorHover: '#3b1f6a',
  submitTextColor: '#ffffff',      submitBorderRadius: 8,
  submitPaddingX: 24,              submitPaddingY: 10,
  submitFontSize: 14,              submitFontWeight: 600,
  submitShadow: 'none',            submitFullWidth: false,
  navButtonBgColor: '#f1f5f9',     navButtonHoverBgColor: '#e2e8f0',
  navButtonTextColor: '#374151',   navButtonBorderRadius: 8,
  navButtonStyle: 'filled',        navButtonFontSize: 13,
  navButtonFontWeight: 500,        navButtonPaddingX: 18,            navButtonPaddingY: 8,
  cancelButtonBgColor: 'transparent', cancelButtonTextColor: '#64748b',
  cancelButtonBorderRadius: 8,     cancelButtonBorderColor: '#e2e8f0',
  cancelButtonStyle: 'ghost',      cancelButtonFontSize: 13,
  cancelButtonFontWeight: 500,     cancelButtonPaddingX: 16,         cancelButtonPaddingY: 8,
  formBgColor: '#ffffff',          formBgOpacity: 100,
  formBorderVisible: false,        formBorderColor: '#e2e8f0',
  formBorderWidth: 1,              formBorderRadius: 12,         formBorderStyle: 'solid',
  formPaddingX: 32,                formPaddingY: 32,
  formShadow: 'none',              formMaxWidth: 600,
  fieldGap: 16,                    rowGap: 24,
  progressFontSize: 12,            progressFontWeight: 500,
  progressBgColor: '#f8fafc',      progressPadding: 16,          progressBorderRadius: 8,
  restoreBannerBgColor: '#eff6ff', restoreBannerTextColor: '#1d4ed8',
  restoreBannerBorderColor: '#bfdbfe', restoreBannerBorderRadius: 8,
  summarySectionLabelColor: '#6b7280', summaryValueColor: '#1e293b',
  summaryEditButtonColor: '#4e2e80',
};

// ─── Preset designs (12 themes) ───────────────────────────────────────────────
export const PRESET_DESIGNS = [
  {
    designId: 'default', designName: 'Default',
    description: 'Clean white form, gray borders, purple focus',
    accentColor: '#4e2e80', isDefault: true, isCustom: false,
    tokens: { ...DEFAULT_TOKENS },
  },
  {
    designId: 'minimal', designName: 'Minimal',
    description: 'No borders, filled inputs, ultra-clean',
    accentColor: '#1e293b', isDefault: false, isCustom: false,
    tokens: {
      ...DEFAULT_TOKENS,
      inputBgColor: '#f1f5f9', inputBgColorFocus: '#ffffff',
      inputBorderWidth: 0, inputBorderRadius: 6,
      inputShadow: 'none', inputShadowFocus: 'ring',
      inputFocusRingColor: '#1e293b', inputFocusRingWidth: 2,
      formBgColor: '#ffffff', formShadow: 'none', formBorderVisible: false,
      labelColor: '#64748b', labelFontWeight: 500,
      submitBgColor: '#1e293b', submitBgColorHover: '#0f172a', fieldGap: 20,
    },
  },
  {
    designId: 'rounded', designName: 'Rounded',
    description: 'Large radius, soft shadows, violet accent',
    accentColor: '#8b5cf6', isDefault: false, isCustom: false,
    tokens: {
      ...DEFAULT_TOKENS,
      inputBorderRadius: 14, inputBorderColorFocus: '#8b5cf6',
      inputFocusRingColor: '#8b5cf6', inputShadow: 'sm', inputShadowFocus: 'ring',
      formBorderRadius: 22, formShadow: 'md',
      formBorderVisible: true, formBorderColor: '#ede9fe',
      submitBgColor: '#8b5cf6', submitBgColorHover: '#7c3aed', submitBorderRadius: 14,
      navButtonBorderRadius: 14,
    },
  },
  {
    designId: 'bold', designName: 'Bold',
    description: 'Dark background, amber highlights, high contrast',
    accentColor: '#f59e0b', isDefault: false, isCustom: false,
    tokens: {
      ...DEFAULT_TOKENS,
      formBgColor: '#1e293b', formBorderRadius: 10, formShadow: 'lg',
      labelColor: '#e2e8f0', labelFontWeight: 700,
      inputBgColor: '#0f172a', inputBgColorFocus: '#1e293b',
      inputTextColor: '#f1f5f9', inputPlaceholderColor: '#475569',
      inputBorderColor: '#334155', inputBorderColorFocus: '#f59e0b',
      inputFocusRingColor: '#f59e0b', inputBorderRadius: 4,
      submitBgColor: '#f59e0b', submitTextColor: '#1e293b',
      submitBorderRadius: 4, submitFontWeight: 700,
      submitPaddingX: 32, submitPaddingY: 12,
      navButtonBgColor: '#334155', navButtonTextColor: '#e2e8f0',
    },
  },
  {
    designId: 'material', designName: 'Material',
    description: 'Floating labels, underline inputs, Google style',
    accentColor: '#1976d2', isDefault: false, isCustom: false,
    tokens: {
      ...DEFAULT_TOKENS,
      labelPosition: 'floating',
      inputBorderWidth: 0, inputBorderRadius: 0,
      inputShadow: 'none', inputShadowFocus: 'none',
      formBgColor: '#ffffff', formShadow: 'none',
      submitBgColor: '#1976d2', submitBgColorHover: '#1565c0', submitBorderRadius: 4,
      navButtonBgColor: 'transparent', navButtonTextColor: '#1976d2', navButtonStyle: 'ghost',
    },
  },
  {
    designId: 'card', designName: 'Card Fields',
    description: 'Each field in its own card, spacious feel',
    accentColor: '#0f766e', isDefault: false, isCustom: false,
    tokens: {
      ...DEFAULT_TOKENS,
      fieldGap: 16, formPaddingX: 16, formPaddingY: 16,
      inputFontSize: 15, labelFontSize: 13,
      inputPaddingX: 16, inputPaddingY: 10, inputHeight: 44,
      inputBorderRadius: 8, inputShadow: 'sm',
      formBgColor: '#f8fafc', formBorderVisible: false,
      submitBgColor: '#0f766e', submitBorderRadius: 8, submitFullWidth: true,
    },
  },
  {
    designId: 'soft', designName: 'Soft',
    description: 'Pastel purple tones, gentle and approachable',
    accentColor: '#a855f7', isDefault: false, isCustom: false,
    tokens: {
      ...DEFAULT_TOKENS,
      inputBgColor: '#faf5ff', inputBorderColor: '#e9d5ff',
      inputBorderColorFocus: '#a855f7', inputFocusRingColor: '#a855f7',
      inputBorderRadius: 10,
      labelColor: '#7e22ce', labelFontWeight: 500,
      submitBgColor: '#a855f7', submitBgColorHover: '#9333ea', submitBorderRadius: 10,
      formBgColor: '#fdf4ff', formBorderVisible: true,
      formBorderColor: '#f3e8ff', formBorderRadius: 16, formShadow: 'sm',
      navButtonBorderRadius: 10,
    },
  },
  {
    designId: 'corporate', designName: 'Corporate',
    description: 'Navy blue, professional and trustworthy',
    accentColor: '#1e3a5f', isDefault: false, isCustom: false,
    tokens: {
      ...DEFAULT_TOKENS,
      labelFontWeight: 700, labelColor: '#1e3a5f',
      inputBorderColor: '#b0c4de', inputBorderColorFocus: '#1e3a5f',
      inputFocusRingColor: '#1e3a5f', inputBorderRadius: 4,
      submitBgColor: '#1e3a5f', submitBgColorHover: '#152d4a',
      submitBorderRadius: 4, navButtonBorderRadius: 4,
      formBorderVisible: true, formBorderColor: '#dde6f0',
      formBorderRadius: 6, formBgColor: '#f8fbff',
    },
  },
  {
    designId: 'warm', designName: 'Warm',
    description: 'Amber tones, cozy and inviting',
    accentColor: '#d97706', isDefault: false, isCustom: false,
    tokens: {
      ...DEFAULT_TOKENS,
      inputBorderColorFocus: '#d97706', inputFocusRingColor: '#d97706',
      inputBgColor: '#fffbeb', inputBorderColor: '#fde68a',
      submitBgColor: '#d97706', submitBgColorHover: '#b45309',
      labelColor: '#92400e', labelFontWeight: 600,
      formBgColor: '#fffbeb', formBorderVisible: true,
      formBorderColor: '#fde68a', formBorderRadius: 10,
    },
  },
  {
    designId: 'midnight', designName: 'Midnight',
    description: 'Deep dark theme, indigo accents',
    accentColor: '#6366f1', isDefault: false, isCustom: false,
    tokens: {
      ...DEFAULT_TOKENS,
      formBgColor: '#0f172a', formBorderRadius: 16, formShadow: 'lg',
      labelColor: '#94a3b8', labelFontWeight: 500,
      inputBgColor: '#1e293b', inputBgColorFocus: '#293548',
      inputTextColor: '#f1f5f9', inputPlaceholderColor: '#475569',
      inputBorderColor: '#334155', inputBorderColorFocus: '#6366f1',
      inputFocusRingColor: '#6366f1', inputBorderRadius: 8,
      submitBgColor: '#6366f1', submitBgColorHover: '#4f46e5', submitBorderRadius: 8,
      navButtonBgColor: '#1e293b', navButtonTextColor: '#94a3b8',
    },
  },
  {
    designId: 'glass', designName: 'Glass',
    description: 'Frosted glass card, modern translucent look',
    accentColor: '#6366f1', isDefault: false, isCustom: false,
    tokens: {
      ...DEFAULT_TOKENS,
      formBgColor: '#ffffff', formBgOpacity: 85,
      formBorderVisible: true, formBorderColor: '#e0e7ff',
      formBorderRadius: 18, formShadow: 'lg',
      inputBgColor: '#f8faff', inputBgColorFocus: '#ffffff',
      inputBorderColor: '#c7d2fe', inputBorderColorFocus: '#6366f1',
      inputFocusRingColor: '#6366f1', inputBorderRadius: 10, inputShadow: 'sm',
      labelColor: '#4338ca', labelFontWeight: 600,
      submitBgColor: '#6366f1', submitBgColorHover: '#4f46e5', submitBorderRadius: 10,
      navButtonBorderRadius: 10,
    },
  },
  {
    designId: 'outlined', designName: 'Outlined',
    description: 'Heavy borders, sharp edges, structured clarity',
    accentColor: '#0f172a', isDefault: false, isCustom: false,
    tokens: {
      ...DEFAULT_TOKENS,
      inputBorderColor: '#475569', inputBorderWidth: 2,
      inputBorderColorFocus: '#0f172a', inputBorderRadius: 4,
      inputShadowFocus: 'none', inputFocusRingWidth: 0,
      labelFontWeight: 700, labelColor: '#0f172a',
      submitBgColor: '#0f172a', submitBorderRadius: 4, submitPaddingX: 28,
      formBorderVisible: true, formBorderColor: '#0f172a',
      formBorderWidth: 2, formBorderRadius: 4,
    },
  },
];


// ─── UI Primitives ────────────────────────────────────────────────────────────

function Toggle({ value, onChange, disabled }) {
  return (
    <div onClick={() => !disabled && onChange(!value)} style={{
      width: 36, height: 20, borderRadius: 10, cursor: disabled ? 'default' : 'pointer',
      background: value ? 'var(--primary-color)' : '#e2e8f0', position: 'relative',
      transition: 'background 0.2s', flexShrink: 0, opacity: disabled ? 0.5 : 1,
    }}>
      <div style={{
        position: 'absolute', top: 3, left: value ? 18 : 3,
        width: 14, height: 14, borderRadius: '50%', background: '#fff',
        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
  );
}

function Seg({ value, onChange, options, small }) {
  return (
    <div style={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
      {options.map(opt => (
        <button key={opt.value} type="button" onClick={() => onChange(opt.value)} style={{
          flex: 1, padding: small ? '3px 0' : '5px 0', border: 'none',
          borderRight: '1px solid #e2e8f0',
          background: value === opt.value ? '#1e293b' : '#fff',
          color: value === opt.value ? '#fff' : '#374151',
          fontSize: small ? 10 : 11, fontWeight: value === opt.value ? 600 : 400,
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3,
        }}>
          {opt.icon && <i className={`bi ${opt.icon}`} style={{ fontSize: small ? 10 : 12 }} />}
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function ColorField({ value, onChange, allowEmpty }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <input type="color" value={value || '#ffffff'} onChange={e => onChange(e.target.value)}
        style={{ width: 32, height: 26, padding: 2, border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer' }} />
      <input type="text" value={value || ''} onChange={e => onChange(e.target.value)}
        placeholder={allowEmpty ? 'Auto' : '#000000'}
        style={{ width: 80, fontSize: 11, padding: '2px 6px', border: '1px solid #e2e8f0', borderRadius: 4, fontFamily: 'monospace' }} />
      {allowEmpty && value && (
        <button type="button" onClick={() => onChange('')}
          style={{ fontSize: 10, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Clear</button>
      )}
    </div>
  );
}

function NumInput({ value, onChange, min, max, suffix, step }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <input type="number" min={min ?? 0} max={max ?? 200} step={step || 1} value={value ?? 0}
        onChange={e => onChange(Number(e.target.value))}
        style={{ width: 64, fontSize: 12, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 4, textAlign: 'right' }} />
      {suffix && <span style={{ fontSize: 11, color: '#94a3b8' }}>{suffix}</span>}
    </div>
  );
}

function IRow({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      {label && <div style={{ fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</div>}
      {hint && <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4 }}>{hint}</div>}
      {children}
    </div>
  );
}

function InlineRow({ label, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 8 }}>
      <span style={{ fontSize: 11, color: '#374151', flexShrink: 0 }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{children}</div>
    </div>
  );
}

function SDiv({ label }) {
  return (
    <div style={{ margin: '14px 0 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
    </div>
  );
}

function Accordion({ label, icon, badge, subtitle, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderBottom: '1px solid #f1f5f9' }}>
      <button type="button" onClick={() => setOpen(o => !o)} style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 0', background: 'none', border: 'none', cursor: 'pointer',
        color: '#1e293b', fontWeight: 600, fontSize: 12,
      }}>
        {icon && <i className={`bi ${icon}`} style={{ fontSize: 13, color: '#64748b' }} />}
        <span style={{ flex: 1, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
          {label}
          {subtitle && (
            <span style={{
              fontSize: 10, fontWeight: 600, color: 'var(--primary-color)',
              background: '#f3eeff', borderRadius: 4, padding: '1px 7px',
              maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              flexShrink: 0,
            }}>{subtitle}</span>
          )}
        </span>
        {badge != null && (
          <span style={{ background: '#f1f5f9', color: '#64748b', fontSize: 9, fontWeight: 600, borderRadius: 4, padding: '1px 6px' }}>{badge}</span>
        )}
        <i className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ fontSize: 11, color: '#94a3b8' }} />
      </button>
      {open && <div style={{ paddingBottom: 14 }}>{children}</div>}
    </div>
  );
}

// ─── Field card ───────────────────────────────────────────────────────────────

function FieldCard({ field, onUpdate, onRemove, showColSpan, maxColSpan }) {
  const [expanded, setExpanded] = useState(false);
  const [rawOptions, setRawOptions] = useState(() => (field.options || []).join(', '));
  const [rawDomains, setRawDomains] = useState(() => (field.allowedDomains || []).join(', '));
  const prevFieldIdRef = useRef(field.id);
  if (prevFieldIdRef.current !== field.id) {
    prevFieldIdRef.current = field.id;
    setRawOptions((field.options || []).join(', '));
    setRawDomains((field.allowedDomains || []).join(', '));
  }
  const meta = TYPE_META[field.type] || TYPE_META.text;

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', marginBottom: 6, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 8px' }}>
        {/* Drag handle */}
        <span style={{ color: '#cbd5e1', cursor: 'grab', fontSize: 13, flexShrink: 0 }}>
          <i className="bi bi-grip-vertical" />
        </span>
        {/* Type badge */}
        <span style={{
          fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, flexShrink: 0,
          background: meta.bg, color: meta.color, whiteSpace: 'nowrap',
        }}>
          <i className={`bi ${meta.icon} me-1`} style={{ fontSize: 9 }} />{meta.label}
        </span>
        {/* Label input */}
        <input className="form-control form-control-sm" style={{ flex: 1, fontSize: 12 }}
          value={field.label} onChange={e => onUpdate({ label: e.target.value })} placeholder="Field label" />
        {/* Required checkbox */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer', flexShrink: 0, fontSize: 10, color: '#64748b' }}>
          <input type="checkbox" checked={field.required || false}
            onChange={e => onUpdate({ required: e.target.checked })} style={{ margin: 0 }} />
          Req
        </label>
        {/* Expand */}
        <button type="button" onClick={() => setExpanded(x => !x)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '1px 4px', fontSize: 11 }}>
          <i className={`bi ${expanded ? 'bi-chevron-up' : 'bi-three-dots'}`} />
        </button>
        {/* Delete */}
        <button type="button" onClick={onRemove}
          style={{ background: '#fee2e2', color: '#dc2626', border: 'none', padding: '2px 6px', borderRadius: 4, cursor: 'pointer', fontSize: 12, flexShrink: 0 }}>
          <i className="bi bi-x" />
        </button>
      </div>

      {/* Expanded: type selector + advanced options */}
      {expanded && (
        <div style={{ borderTop: '1px solid #f8fafc', padding: '8px 10px', background: '#fafafa' }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            {FIELD_TYPES.map(t => (
              <button key={t.value} type="button" onClick={() => onUpdate({ type: t.value })}
                style={{
                  fontSize: 10, padding: '2px 8px', borderRadius: 4, border: '1px solid',
                  borderColor: field.type === t.value ? t.color : '#e2e8f0',
                  background: field.type === t.value ? t.bg : '#fff',
                  color: field.type === t.value ? t.color : '#64748b',
                  cursor: 'pointer', fontWeight: field.type === t.value ? 700 : 400,
                }}>
                <i className={`bi ${t.icon} me-1`} style={{ fontSize: 9 }} />{t.label}
              </button>
            ))}
          </div>
          {field.type === 'lookup' && (
            <LookupFieldConfig field={field} onUpdate={onUpdate} />
          )}
          {(field.type === 'select' || field.type === 'radio') && (
            <IRow label="Options (comma separated)">
              <input className="form-control form-control-sm" style={{ fontSize: 11 }}
                placeholder="Option A, Option B, Option C"
                value={rawOptions}
                onChange={e => setRawOptions(e.target.value)}
                onBlur={e => {
                  const parsed = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                  onUpdate({ options: parsed });
                  setRawOptions(parsed.join(', '));
                }} />
            </IRow>
          )}
          {/*
            Which addresses this field will take. Stored on the field rather
            than in `rules`, which the Data tab overwrites wholesale whenever
            the mapping changes — an allowlist typed here would disappear the
            next time somebody re-mapped the column.
          */}
          {field.type === 'email' && (
            <IRow label="Allowed domains"
              hint="Comma separated, e.g. company.com, gmail.com. Leave blank or type * to accept any domain.">
              <input className="form-control form-control-sm" style={{ fontSize: 11 }}
                placeholder="Any domain"
                value={rawDomains}
                onChange={e => setRawDomains(e.target.value)}
                onBlur={e => {
                  const parsed = e.target.value
                    .split(',')
                    // The @ is what somebody types when the label says "domain";
                    // it is stripped rather than refused.
                    .map(s => s.trim().toLowerCase().replace(/^@+/, ''))
                    .filter(Boolean);
                  onUpdate({ allowedDomains: parsed });
                  setRawDomains(parsed.join(', '));
                }} />
            </IRow>
          )}
          <IRow label="Placeholder">
            <input className="form-control form-control-sm" style={{ fontSize: 11 }}
              value={field.placeholder || ''} placeholder="Placeholder text…"
              onChange={e => onUpdate({ placeholder: e.target.value })} />
          </IRow>
          <IRow label="Help text">
            <input className="form-control form-control-sm" style={{ fontSize: 11 }}
              value={field.helpText || ''} placeholder="Shown below the field"
              onChange={e => onUpdate({ helpText: e.target.value })} />
          </IRow>
          {showColSpan && (
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, color: '#374151', fontWeight: 600, marginBottom: 4 }}>Column span</div>
                <Seg value={field.columnSpan || 1} onChange={v => onUpdate({ columnSpan: v, fullWidth: false })}
                  options={[1, 2, 3, 4].filter(n => n <= (maxColSpan || 4)).map(n => ({ value: n, label: String(n) }))} small />
              </div>
              <div style={{ flexShrink: 0, paddingTop: 14 }}>
                <InlineRow label="Full width">
                  <Toggle value={field.fullWidth || false} onChange={v => onUpdate({ fullWidth: v, columnSpan: 1 })} />
                </InlineRow>
              </div>
            </div>
          )}
          <InlineRow label="Mark as sensitive (hidden in summaries)">
            <Toggle value={field.isSensitive || false} onChange={v => onUpdate({ isSensitive: v })} />
          </InlineRow>
        </div>
      )}
    </div>
  );
}

// ─── Row card ─────────────────────────────────────────────────────────────────

function RowCard({ row, rowIndex, onUpdateRow, onRemoveRow, onConfirmRemoveField }) {
  const dragIdx = useRef(null);
  const cols = row.fieldsPerRow || 2;

  const updateField = (fid, patch) =>
    onUpdateRow({ fields: row.fields.map(f => f.id === fid ? { ...f, ...patch } : f) });
  const removeField = fid => {
    const doRemove = () => onUpdateRow({ fields: row.fields.filter(f => f.id !== fid) });
    if (onConfirmRemoveField) {
      onConfirmRemoveField(doRemove);
    } else {
      doRemove();
    }
  };
  const addField    = () => onUpdateRow({ fields: [...row.fields, newField(`Field ${row.fields.length + 1}`)] });

  const handleDrop = (dropI) => {
    if (dragIdx.current === null || dragIdx.current === dropI) return;
    const list = [...row.fields];
    const [moved] = list.splice(dragIdx.current, 1);
    list.splice(dropI > dragIdx.current ? dropI - 1 : dropI, 0, moved);
    onUpdateRow({ fields: list });
    dragIdx.current = null;
  };

  const isFull = row.fields.length >= cols;

  return (
    <div style={{ border: '1px dashed #cbd5e1', borderRadius: 10, background: '#f8fafc', marginBottom: 10 }}>
      {/* Row header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px', borderBottom: '1px solid #eef2f7' }}>
        <span style={{ fontSize: 9, fontWeight: 700, color: '#64748b', background: '#e2e8f0', borderRadius: 4, padding: '1px 6px', flexShrink: 0 }}>Row {rowIndex + 1}</span>
        <span style={{ fontSize: 10, color: '#94a3b8', flexShrink: 0 }}>Columns:</span>
        {[1, 2, 3, 4].map(n => (
          <button key={n} type="button" onClick={() => onUpdateRow({ fieldsPerRow: n })} style={{
            width: 22, height: 22, border: '1px solid #e2e8f0', borderRadius: 4,
            background: cols === n ? '#1e293b' : '#fff', color: cols === n ? '#fff' : '#64748b',
            fontSize: 10, fontWeight: 600, cursor: 'pointer', padding: 0, flexShrink: 0,
          }}>{n}</button>
        ))}
        {/* Visual grid sketch */}
        <div style={{ display: 'flex', gap: 2, flex: 1 }}>
          {Array.from({ length: cols }).map((_, i) => (
            <div key={i} style={{
              flex: 1, height: 8, borderRadius: 2, maxWidth: 20,
              background: i < row.fields.length ? '#94a3b8' : '#e2e8f0',
            }} />
          ))}
        </div>
        <span style={{ fontSize: 9, color: '#94a3b8' }}>{row.fields.length}/{cols}</span>
        <button type="button" onClick={onRemoveRow}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', padding: 0, fontSize: 13, flexShrink: 0 }}>
          <i className="bi bi-trash" />
        </button>
      </div>

      {/* Fields in row */}
      <div style={{ padding: '8px 10px' }}>
        {row.fields.length === 0 && (
          <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 11, padding: '6px 0' }}>
            No fields. Click "Add field" below.
          </div>
        )}
        {row.fields.map((field, fi) => (
          <div key={field.id} draggable
            onDragStart={() => { dragIdx.current = fi; }}
            onDragOver={e => e.preventDefault()}
            onDrop={() => handleDrop(fi)}>
            <FieldCard field={field} onUpdate={p => updateField(field.id, p)}
              onRemove={() => removeField(field.id)} showColSpan maxColSpan={cols} />
          </div>
        ))}
        <button type="button" onClick={addField} disabled={isFull} style={{
          width: '100%', border: '1px dashed #cbd5e1', borderRadius: 6,
          background: 'none', color: isFull ? '#cbd5e1' : '#64748b', fontSize: 11,
          padding: '5px 0', cursor: isFull ? 'not-allowed' : 'pointer',
        }}>
          <i className="bi bi-plus me-1" />
          {isFull ? 'Row is full' : `Add field (${row.fields.length}/${cols})`}
        </button>
      </div>
    </div>
  );
}

// ─── Stage card ───────────────────────────────────────────────────────────────

function StageCard({ stage, stageIndex, stageCount, onUpdate, onRemove, onMoveUp, onMoveDown, onConfirmRemoveField }) {
  const [expanded, setExpanded] = useState(stageIndex === 0);
  const [tab, setTab] = useState('fields');

  const updateRow    = (ri, patch) => onUpdate({ rows: stage.rows.map((r, i) => i === ri ? { ...r, ...patch } : r) });
  const removeRow    = (ri) => onUpdate({ rows: stage.rows.filter((_, i) => i !== ri) });
  const addRow       = () => onUpdate({ rows: [...stage.rows, newRow(2)] });
  const stageFieldCount = (stage.rows || []).reduce((n, r) => n + (r.fields || []).length, 0);

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 10, background: '#fff', overflow: 'hidden' }}>
      {/* Stage header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', background: expanded ? '#fafafa' : '#fff' }}>
        <span style={{ color: '#cbd5e1', cursor: 'grab', fontSize: 14, flexShrink: 0 }}>
          <i className="bi bi-grip-vertical" />
        </span>
        {/* Number badge */}
        <span style={{
          fontSize: 10, fontWeight: 700, color: '#fff', background: 'var(--primary-color)',
          borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>{stageIndex + 1}</span>
        {/* Label */}
        <input className="form-control form-control-sm" style={{ flex: 1, fontSize: 12, fontWeight: 600, border: 'none', background: 'transparent', padding: '0 4px' }}
          value={stage.stageLabel} onChange={e => onUpdate({ stageLabel: e.target.value })} />
        {/* Field count badge */}
        <span style={{ fontSize: 9, color: '#94a3b8', background: '#f1f5f9', borderRadius: 4, padding: '1px 5px', flexShrink: 0 }}>
          {stageFieldCount} field{stageFieldCount !== 1 ? 's' : ''}
        </span>
        {/* Move up/down */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <button type="button" onClick={onMoveUp} disabled={stageIndex === 0}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, fontSize: 10, lineHeight: 1, opacity: stageIndex === 0 ? 0.3 : 1 }}>
            <i className="bi bi-chevron-up" />
          </button>
          <button type="button" onClick={onMoveDown} disabled={stageIndex >= stageCount - 1}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, fontSize: 10, lineHeight: 1, opacity: stageIndex >= stageCount - 1 ? 0.3 : 1 }}>
            <i className="bi bi-chevron-down" />
          </button>
        </div>
        <button type="button" onClick={() => setExpanded(x => !x)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '2px 6px', fontSize: 12, flexShrink: 0 }}>
          <i className={`bi ${expanded ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
        </button>
        <button type="button" onClick={onRemove} disabled={stageCount <= 1}
          style={{ background: '#fee2e2', color: '#dc2626', border: 'none', padding: '2px 7px', borderRadius: 4, cursor: stageCount <= 1 ? 'not-allowed' : 'pointer', fontSize: 12, opacity: stageCount <= 1 ? 0.4 : 1, flexShrink: 0 }}>
          <i className="bi bi-trash" />
        </button>
      </div>

      {expanded && (
        <div style={{ borderTop: '1px solid #f1f5f9' }}>
          {/* Sub-tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid #f1f5f9' }}>
            {[{ id: 'fields', label: 'Fields & Rows', icon: 'bi-layout-three-columns' }, { id: 'settings', label: 'Stage Settings', icon: 'bi-gear' }].map(t => (
              <button key={t.id} type="button" onClick={() => setTab(t.id)} style={{
                flex: 1, padding: '7px 0', border: 'none',
                borderBottom: tab === t.id ? '2px solid var(--primary-color)' : '2px solid transparent',
                background: 'none', color: tab === t.id ? 'var(--primary-color)' : '#64748b',
                fontSize: 11, fontWeight: tab === t.id ? 600 : 400, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
              }}>
                <i className={`bi ${t.icon}`} style={{ fontSize: 11 }} />{t.label}
              </button>
            ))}
          </div>

          <div style={{ padding: '10px 12px' }}>
            {tab === 'fields' && (
              <>
                {stage.rows.map((row, ri) => (
                  <RowCard key={row.rowId} row={row} rowIndex={ri}
                    onUpdateRow={p => updateRow(ri, p)} onRemoveRow={() => removeRow(ri)}
                    onConfirmRemoveField={onConfirmRemoveField} />
                ))}
                <button type="button" onClick={addRow} style={{
                  width: '100%', border: '1px dashed #94a3b8', borderRadius: 6,
                  background: 'none', color: '#64748b', fontSize: 11, padding: '6px 0', cursor: 'pointer', marginTop: 4,
                }}>
                  <i className="bi bi-plus me-1" />Add row to this stage
                </button>
              </>
            )}
            {tab === 'settings' && (
              <>
                <IRow label="Stage description">
                  <input className="form-control form-control-sm" style={{ fontSize: 11 }}
                    value={stage.stageDescription || ''} placeholder="Optional subtitle shown under stage label"
                    onChange={e => onUpdate({ stageDescription: e.target.value })} />
                </IRow>
                <IRow label="Validation mode">
                  <select className="form-select form-select-sm" style={{ fontSize: 11 }} value={stage.validationMode || 'on-next'}
                    onChange={e => onUpdate({ validationMode: e.target.value })}>
                    <option value="on-next">Validate when Next is clicked</option>
                    <option value="on-blur">Validate on field blur</option>
                    <option value="on-change">Validate on every change</option>
                  </select>
                </IRow>
                <SDiv label="Button Labels" />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <IRow label="Next label">
                    <input className="form-control form-control-sm" style={{ fontSize: 11 }} value={stage.nextLabel || 'Next'}
                      onChange={e => onUpdate({ nextLabel: e.target.value })} />
                  </IRow>
                  <IRow label="Back label">
                    <input className="form-control form-control-sm" style={{ fontSize: 11 }} value={stage.backLabel || 'Back'}
                      onChange={e => onUpdate({ backLabel: e.target.value })} />
                  </IRow>
                </div>
                <InlineRow label="Allow skip this stage">
                  <Toggle value={stage.allowSkip || false} onChange={v => onUpdate({ allowSkip: v })} />
                </InlineRow>
                {stage.allowSkip && (
                  <IRow label="Skip label">
                    <input className="form-control form-control-sm" style={{ fontSize: 11 }} value={stage.skipLabel || 'Skip'}
                      onChange={e => onUpdate({ skipLabel: e.target.value })} />
                  </IRow>
                )}
                <SDiv label="On Stage Complete" />
                <InlineRow label="Show summary on next stage">
                  <Toggle value={stage.summaryOnComplete || false} onChange={v => onUpdate({ summaryOnComplete: v })} />
                </InlineRow>
                <InlineRow label="Auto-save progress to browser">
                  <Toggle value={stage.autoSaveOnComplete !== false} onChange={v => onUpdate({ autoSaveOnComplete: v })} />
                </InlineRow>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Design card (grid layout) ────────────────────────────────────────────────

function DesignCard({ design, isActive, onSelect, onEdit, onDelete }) {
  const t = { ...DEFAULT_TOKENS, ...design.tokens };
  const [hovered, setHovered] = useState(false);

  const isMat  = t.inputBorderWidth === 0;
  const shadowVal = s => s === 'sm' ? '0 1px 4px rgba(0,0,0,0.08)' : s === 'md' ? '0 4px 12px rgba(0,0,0,0.10)' : s === 'lg' ? '0 8px 20px rgba(0,0,0,0.14)' : 'none';

  return (
    <div
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        border: isActive ? `2px solid ${design.accentColor || '#4e2e80'}` : '1px solid #e2e8f0',
        borderRadius: 10, overflow: 'hidden', cursor: 'pointer',
        boxShadow: isActive ? `0 0 0 3px ${design.accentColor || '#4e2e80'}25` : hovered ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
        transition: 'box-shadow 0.15s, border-color 0.15s',
        position: 'relative',
      }}>

      {/* Active checkmark */}
      {isActive && (
        <div style={{
          position: 'absolute', top: 6, right: 6, zIndex: 2,
          background: design.accentColor || '#4e2e80', color: '#fff',
          borderRadius: '50%', width: 18, height: 18,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10,
        }}>
          <i className="bi bi-check" />
        </div>
      )}

      {/* Visual preview area */}
      <div style={{
        background: t.formBgColor, padding: '10px 10px 8px',
        boxShadow: shadowVal(t.formShadow),
        minHeight: 80,
      }}>
        {/* Label mock */}
        <div style={{ fontSize: 8, fontWeight: t.labelFontWeight, color: t.labelColor, marginBottom: 4, opacity: 0.9 }}>Your name</div>
        {/* Input mock */}
        <div style={{
          height: 20, width: '100%', marginBottom: 6,
          background: t.inputBgColor,
          border: isMat ? 'none' : `${t.inputBorderWidth}px ${t.inputBorderStyle} ${t.inputBorderColor}`,
          borderBottom: isMat ? `2px solid ${t.inputBorderColor}` : undefined,
          borderRadius: isMat ? 0 : Math.min(t.inputBorderRadius, 8),
        }} />
        {/* Label 2 mock */}
        <div style={{ fontSize: 8, fontWeight: t.labelFontWeight, color: t.labelColor, marginBottom: 4, opacity: 0.9 }}>Message</div>
        {/* Textarea mock */}
        <div style={{
          height: 16, width: '100%', marginBottom: 8,
          background: t.inputBgColor,
          border: isMat ? 'none' : `${t.inputBorderWidth}px ${t.inputBorderStyle} ${t.inputBorderColor}`,
          borderBottom: isMat ? `2px solid ${t.inputBorderColor}` : undefined,
          borderRadius: isMat ? 0 : Math.min(t.inputBorderRadius, 8),
        }} />
        {/* Submit mock */}
        <div style={{
          display: 'inline-block', padding: '3px 10px',
          background: t.submitBgColor, color: t.submitTextColor,
          borderRadius: Math.min(t.submitBorderRadius, 8),
          fontSize: 8, fontWeight: t.submitFontWeight,
          width: t.submitFullWidth ? '100%' : 'auto', textAlign: 'center',
        }}>Submit</div>
      </div>

      {/* Footer: name + actions */}
      <div style={{
        padding: '6px 8px',
        background: isActive ? (design.accentColor || '#4e2e80') : '#f8fafc',
        borderTop: '1px solid #f0f0f0',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: isActive ? '#fff' : '#1e293b', lineHeight: 1.2 }}>{design.designName}</div>
          {design.description && (
            <div style={{ fontSize: 8, color: isActive ? 'rgba(255,255,255,0.75)' : '#94a3b8', lineHeight: 1.3, marginTop: 1 }}>{design.description}</div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
          {design.isDefault && (
            <span style={{ fontSize: 8, background: isActive ? 'rgba(255,255,255,0.2)' : '#e0e7ff', color: isActive ? '#fff' : '#4338ca', borderRadius: 3, padding: '1px 4px', fontWeight: 600 }}>Built-in</span>
          )}
          {design.isCustom && (
            <>
              <button type="button" onClick={e => { e.stopPropagation(); onEdit?.(); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', color: isActive ? 'rgba(255,255,255,0.8)' : '#64748b', fontSize: 11, lineHeight: 1 }}>
                <i className="bi bi-pencil" />
              </button>
              <button type="button" onClick={e => { e.stopPropagation(); onDelete?.(); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', color: '#dc2626', fontSize: 11, lineHeight: 1 }}>
                <i className="bi bi-trash" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Design token editor (sub-panel) ─────────────────────────────────────────

const TOKEN_GROUPS = [
  { id: 'fields',    label: 'Fields',        icon: 'bi-input-cursor-text' },
  { id: 'text',      label: 'Text & Labels', icon: 'bi-fonts' },
  { id: 'buttons',   label: 'Buttons',       icon: 'bi-hand-index' },
  { id: 'validation',label: 'Validation',    icon: 'bi-shield-check' },
  { id: 'layout',    label: 'Card & Layout', icon: 'bi-layout-split' },
  { id: 'multistep', label: 'Multi-step',    icon: 'bi-list-ol' },
];

// ─── Visual helper components for token editor ────────────────────────────────

// Segmented button row — use for style/size/position choices
function VisualSeg({ value, onChange, options }) {
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {options.map(o => (
        <button key={o.value} type="button" onClick={() => onChange(o.value)} style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 3, padding: '6px 10px', borderRadius: 7, cursor: 'pointer', flexShrink: 0,
          border: value === o.value ? '2px solid var(--primary-color)' : '1.5px solid #e2e8f0',
          background: value === o.value ? '#f5f0ff' : '#fafafa',
          color: value === o.value ? 'var(--primary-color)' : '#475569',
          fontWeight: value === o.value ? 700 : 400, fontSize: 11,
          minWidth: 52,
        }}>
          {o.icon && <i className={`bi ${o.icon}`} style={{ fontSize: 13 }} />}
          {o.preview && <div style={{ ...o.preview, width: 28, height: 14 }} />}
          <span>{o.label}</span>
        </button>
      ))}
    </div>
  );
}

// Shadow picker — renders actual box-shadow so you can see what you're choosing
const SHADOW_OPTIONS = [
  { value: 'none', label: 'None',     shadow: 'none' },
  { value: 'sm',   label: 'Subtle',   shadow: '0 1px 4px rgba(0,0,0,0.08)' },
  { value: 'md',   label: 'Soft',     shadow: '0 4px 14px rgba(0,0,0,0.10)' },
  { value: 'lg',   label: 'Strong',   shadow: '0 8px 28px rgba(0,0,0,0.15)' },
];
function ShadowPicker({ value, onChange, allowFrom }) {
  const opts = allowFrom
    ? [{ value: '', label: 'From design', shadow: 'none' }, ...SHADOW_OPTIONS]
    : SHADOW_OPTIONS;
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {opts.map(o => {
        const active = (value ?? '') === o.value;
        return (
          <button key={o.value} type="button" onClick={() => onChange(o.value)} style={{
            width: 56, padding: '6px 4px', borderRadius: 8, cursor: 'pointer', textAlign: 'center',
            border: active ? '2px solid var(--primary-color)' : '1.5px solid #e2e8f0',
            background: active ? '#f5f0ff' : '#fff',
          }}>
            <div style={{
              width: 36, height: 22, borderRadius: 4, background: '#fff',
              border: '1px solid #e2e8f0', boxShadow: o.shadow,
              margin: '0 auto 4px',
            }} />
            <span style={{ fontSize: 10, color: active ? 'var(--primary-color)' : '#64748b', fontWeight: active ? 700 : 400 }}>{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// Corner-radius picker — shows shape preview
const RADIUS_OPTIONS = [
  { value: 0,  label: 'None',   style: { borderRadius: 0 } },
  { value: 4,  label: 'Small',  style: { borderRadius: 4 } },
  { value: 8,  label: 'Medium', style: { borderRadius: 8 } },
  { value: 16, label: 'Large',  style: { borderRadius: 16 } },
];
function RadiusPicker({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {RADIUS_OPTIONS.map(o => {
        const active = Number(value) === o.value;
        return (
          <button key={o.value} type="button" onClick={() => onChange(o.value)} style={{
            width: 52, padding: '6px 4px', borderRadius: 7, cursor: 'pointer', textAlign: 'center',
            border: active ? '2px solid var(--primary-color)' : '1.5px solid #e2e8f0',
            background: active ? '#f5f0ff' : '#fafafa',
          }}>
            <div style={{
              width: 28, height: 18, background: active ? 'var(--primary-color)' : '#e2e8f0',
              margin: '0 auto 4px', opacity: active ? 0.4 : 1, ...o.style,
            }} />
            <span style={{ fontSize: 10, color: active ? 'var(--primary-color)' : '#64748b', fontWeight: active ? 700 : 400 }}>{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function hexToRgba(hex, alpha) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return hex;
  return `rgba(${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}, ${alpha})`;
}

// ─── Token editor context + stable micro-components ──────────────────────────
// Defining these outside DesignTokenEditor gives them stable references across
// re-renders, so React updates them in-place instead of unmounting/remounting,
// which would otherwise reset the controls panel scroll position on every change.

const TokenEditorCtx = React.createContext(null);

const _rowStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '10px 0', borderBottom: '1px solid #f1f5f9', gap: 12,
};
const _numStyle = { fontSize: 13, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: 6, textAlign: 'right', width: 72, background: '#fafafa' };
const _cfgTextStyle = { fontSize: 13, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fafafa', minWidth: 160, width: 180 };

function TRow({ label, hint, children }) {
  return (
    <div style={_rowStyle}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: '#1e293b', fontWeight: 500 }}>{label}</div>
        {hint && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{hint}</div>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>{children}</div>
    </div>
  );
}
function PairRow({ label, hint, children }) {
  return (
    <div style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
      <div style={{ fontSize: 13, color: '#1e293b', fontWeight: 500, marginBottom: 6 }}>{label}</div>
      {hint && <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>{hint}</div>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{children}</div>
    </div>
  );
}
function SecHead({ label, hint }) {
  return (
    <div style={{ padding: '16px 0 6px', borderBottom: '2px solid #f1f5f9', marginBottom: 2 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}</div>
      {hint && <div style={{ fontSize: 10, color: '#b0bac6', marginTop: 2 }}>{hint}</div>}
    </div>
  );
}
function NumTok({ k, label, min = 0, max = 200, suffix }) {
  const { tokens, set } = React.useContext(TokenEditorCtx);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      {label && <span style={{ fontSize: 11, color: '#64748b' }}>{label}</span>}
      <input type="number" min={min} max={max} value={tokens[k] ?? 0}
        onChange={e => set({ [k]: Number(e.target.value) })} style={_numStyle} />
      {suffix && <span style={{ fontSize: 11, color: '#94a3b8' }}>{suffix}</span>}
    </div>
  );
}
function ColTok({ k }) {
  const { tokens, set } = React.useContext(TokenEditorCtx);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <input type="color" value={tokens[k] || '#ffffff'} onChange={e => set({ [k]: e.target.value })}
        style={{ width: 34, height: 30, padding: 2, border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer' }} />
      <input type="text" value={tokens[k] || ''} onChange={e => set({ [k]: e.target.value })}
        style={{ ..._numStyle, width: 88, fontFamily: 'monospace', fontSize: 12, textTransform: 'uppercase' }} />
    </div>
  );
}
function BoolTok({ k }) {
  const { tokens, set } = React.useContext(TokenEditorCtx);
  return <Toggle value={!!tokens[k]} onChange={v => set({ [k]: v })} />;
}
function SelTok({ k, options }) {
  const { tokens, set } = React.useContext(TokenEditorCtx);
  return (
    <select value={tokens[k] ?? ''} onChange={e => set({ [k]: e.target.value })}
      style={{ fontSize: 13, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fafafa', minWidth: 120 }}>
      {options.map(o => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
    </select>
  );
}
function CfgText({ k, placeholder }) {
  const { formCfg, handleCfgChange } = React.useContext(TokenEditorCtx);
  return (
    <input type="text" value={formCfg[k] || ''} placeholder={placeholder || ''}
      onChange={e => handleCfgChange({ [k]: e.target.value })}
      style={_cfgTextStyle} />
  );
}
function CfgColor({ k }) {
  const { formCfg, handleCfgChange } = React.useContext(TokenEditorCtx);
  const val = formCfg[k] || '';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <input type="color" value={val || '#ffffff'} onChange={e => handleCfgChange({ [k]: e.target.value })}
        style={{ width: 34, height: 30, padding: 2, border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', opacity: val ? 1 : 0.35 }} />
      <input type="text" value={val} onChange={e => handleCfgChange({ [k]: e.target.value })}
        placeholder="From design" style={{ ..._numStyle, width: 100, fontFamily: 'monospace', fontSize: 12, textAlign: 'left', textTransform: val ? 'uppercase' : 'none' }} />
      {val && (
        <button type="button" title="Clear override" onClick={() => handleCfgChange({ [k]: '' })}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 14, padding: '0 2px', lineHeight: 1 }}>
          <i className="bi bi-x" />
        </button>
      )}
    </div>
  );
}
function CfgBool({ k }) {
  const { formCfg, handleCfgChange } = React.useContext(TokenEditorCtx);
  return <Toggle value={!!formCfg[k]} onChange={v => handleCfgChange({ [k]: v })} />;
}
function CfgNum({ cfgKey, min = 0, max = 200, placeholder }) {
  const { formCfg, handleCfgChange } = React.useContext(TokenEditorCtx);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <input type="number" min={min} max={max} value={formCfg[cfgKey] ?? ''}
        placeholder={placeholder || 'Auto'} onChange={e => handleCfgChange({ [cfgKey]: e.target.value === '' ? undefined : Number(e.target.value) })}
        style={{ width: 64, fontSize: 12, padding: '4px 7px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fafafa', textAlign: 'right' }} />
      <span style={{ fontSize: 11, color: '#94a3b8' }}>px</span>
    </div>
  );
}
function CfgSel({ cfgKey, opts, dflt }) {
  const { formCfg, handleCfgChange } = React.useContext(TokenEditorCtx);
  return (
    <select value={formCfg[cfgKey] || dflt || ''} onChange={e => handleCfgChange({ [cfgKey]: e.target.value })}
      style={{ fontSize: 12, padding: '4px 7px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fafafa' }}>
      {opts.map(o => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
    </select>
  );
}

function DesignTokenEditor({ design, cfg, onCfgChange, onSave, onBack }) {
  const { theme } = useTheme();
  const [tokens, setTokens] = useState({ ...DEFAULT_TOKENS, ...design.tokens });
  const [name, setName]     = useState(design.designName);
  const [group, setGroup]   = useState('fields');
  const set = patch => setTokens(t => ({ ...t, ...patch }));
  const t = tokens;

  // Local cfg copy — updates immediately via handleCfgChange so the preview is always in sync
  const [localCfg, setLocalCfg] = useState(() => cfg || {});
  useEffect(() => { setLocalCfg(cfg || {}); }, [cfg]);
  const handleCfgChange = (patch) => {
    setLocalCfg(prev => ({ ...prev, ...patch }));
    onCfgChange?.(patch);
  };

  const formCfg = localCfg;
  const previewFields = (() => {
    // Check all stages (regardless of stagesEnabled flag)
    const stgFields = (formCfg.stages || []).flatMap(s => (s.rows || []).flatMap(r => r.fields || []));
    if (stgFields.length > 0) return stgFields.slice(0, 4);
    // Check all rows (regardless of rowLayoutEnabled flag)
    const rowFields = (formCfg.rows || []).flatMap(r => r.fields || []);
    if (rowFields.length > 0) return rowFields.slice(0, 4);
    return (formCfg.fields || []).slice(0, 4);
  })();
  const showProgress = !!formCfg.stagesEnabled;
  const showBanner   = !!formCfg.enableLocalPersistence;
  const isMultiStep  = !!formCfg.stagesEnabled;
  const stages       = formCfg.stages || [];
  const submitLabel  = isMultiStep
    ? (stages[0]?.nextLabel || formCfg.defaultNextLabel || 'Next')
    : (formCfg.button_text || formCfg.submitLabel || 'Submit');

  // ── Data for selects ─────────────────────────────────────────────────────────
  const WtOpts   = [{ value: 400, label: 'Regular (400)' }, { value: 500, label: 'Medium (500)' }, { value: 600, label: 'Semi-bold (600)' }, { value: 700, label: 'Bold (700)' }];
  const ShadOpts = [{ value: 'none', label: 'None' }, { value: 'sm', label: 'Small' }, { value: 'md', label: 'Medium' }, { value: 'lg', label: 'Large' }];
  const FontFamilyOpts = [
    { value: '',                        label: 'Page default' },
    { value: 'Inter, sans-serif',       label: 'Inter' },
    { value: 'Arial, sans-serif',       label: 'Arial' },
    { value: 'Georgia, serif',          label: 'Georgia' },
    { value: 'Courier New, monospace',  label: 'Courier New' },
    { value: 'system-ui, sans-serif',   label: 'System UI' },
  ];
  const FontStyleOpts = [{ value: 'normal', label: 'Normal' }, { value: 'italic', label: 'Italic' }];

  // ── Live preview ─────────────────────────────────────────────────────────────
  const isMat = t.inputBorderWidth === 0;
  const shadowMap = { none: 'none', sm: '0 1px 4px rgba(0,0,0,0.07)', md: '0 4px 16px rgba(0,0,0,0.10)', lg: '0 8px 32px rgba(0,0,0,0.14)' };
  const inputBase = {
    background: t.inputBgColor,
    border: isMat ? 'none' : `${t.inputBorderWidth}px ${t.inputBorderStyle} ${t.inputBorderColor}`,
    borderBottom: isMat ? `2px solid ${t.inputBorderColor}` : undefined,
    borderRadius: isMat ? 0 : t.inputBorderRadius,
    padding: `${t.inputPaddingY}px ${t.inputPaddingX}px`,
    fontSize: t.inputPlaceholderFontSize || t.inputFontSize,
    color: t.inputPlaceholderColor,
    height: t.inputHeight, width: '100%', boxSizing: 'border-box',
    display: 'flex', alignItems: 'center',
    transition: `all ${t.inputTransitionDuration}ms ease`,
    fontFamily: t.inputPlaceholderFontFamily || t.inputFontFamily || 'inherit',
    fontWeight: t.inputPlaceholderFontWeight || t.inputFontWeight,
    fontStyle: t.inputPlaceholderFontStyle || 'normal',
    boxShadow: shadowMap[t.inputShadow] || 'none',
  };
  const labelBase = {
    fontSize: t.labelFontSize, fontWeight: t.labelFontWeight, color: t.labelColor,
    fontFamily: t.labelFontFamily || 'inherit', fontStyle: t.labelFontStyle || 'normal',
    textAlign: t.labelTextAlign || 'left',
    marginBottom: t.labelMarginBottom, display: 'block',
  };
  const submitStyle = {
    display: 'inline-block',
    background: formCfg.button_color || t.submitBgColor,
    color: formCfg.button_text_color || t.submitTextColor,
    borderRadius: t.submitBorderRadius, border: 'none',
    padding: `${t.submitPaddingY}px ${t.submitPaddingX}px`,
    fontSize: t.submitFontSize, fontWeight: t.submitFontWeight, cursor: 'pointer',
    width: (formCfg.submit_full_width || t.submitFullWidth) ? '100%' : 'auto', textAlign: 'center',
    boxShadow: shadowMap[t.submitShadow] || 'none',
    marginTop: 8,
  };

  // Renders a set of rows using CSS grid — mirrors the client's RowGrid component
  const renderPreviewRowGrid = (rows) => (
    <div>
      {(rows || []).map((row, ri) => {
        const cols   = row.fieldsPerRow || 1;
        const fields = row.fields || [];
        if (fields.length === 0) return null;
        return (
          <div key={row.rowId || row.id || ri} style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gap: `${t.fieldGap}px ${row.gap || 12}px`,
            marginBottom: row.verticalSpacing || t.fieldGap,
          }}>
            {fields.map(f => (
              <div key={f.id} style={{
                gridColumn: f.fullWidth ? '1 / -1'
                  : (f.columnSpan > 1 ? `span ${Math.min(f.columnSpan, cols)}` : 'span 1'),
              }}>
                <PreviewField field={f} />
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );

  const PreviewField = ({ field }) => {
    const label       = field?.label || 'Field';
    const placeholder = field?.placeholder || (field?.type === 'email' ? 'email@example.com' : `Enter ${label.toLowerCase()}`);
    const isReq       = field?.required !== false;
    const isArea      = field?.type === 'textarea';
    const isCheck     = field?.type === 'checkbox' || field?.type === 'radio';
    if (isCheck) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: field.type === 'radio' ? 16 : 14, height: field.type === 'radio' ? 16 : 14, border: `${t.inputBorderWidth}px solid ${t.inputBorderColor}`, borderRadius: field.type === 'radio' ? '50%' : t.inputBorderRadius / 2, background: t.inputBgColor, flexShrink: 0 }} />
          <label style={{ ...labelBase, marginBottom: 0 }}>{label}{isReq && <span style={{ color: t.requiredMarkColor }}> *</span>}</label>
        </div>
      );
    }
    return (
      <div>
        {!isMat && <label style={labelBase}>{label}{isReq && <span style={{ color: t.requiredMarkColor }}> *</span>}</label>}
        <div style={{ position: 'relative' }}>
          <div style={{ ...inputBase, height: isArea ? t.inputHeight * 2.2 : t.inputHeight, alignItems: isArea ? 'flex-start' : 'center' }}>{placeholder}</div>
          {isMat && <div style={{ position: 'absolute', top: 0, left: t.inputPaddingX, fontSize: t.labelFontSize * 0.85, color: t.labelColor, fontWeight: t.labelFontWeight }}>{label}{isReq && ' *'}</div>}
        </div>
      </div>
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9998, display: 'flex', flexDirection: 'column', background: '#f1f5f9' }}>

      {/* ── Header ───────────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '0 20px', height: 62, flexShrink: 0,
        background: theme.gradientBackground,
        boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
      }}>
        <button type="button" onClick={onBack}
          style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, color: '#fff', padding: '6px 14px', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500, flexShrink: 0 }}>
          <i className="bi bi-arrow-left" /> Back
        </button>
        <div style={{ width: 1, height: 28, background: 'rgba(255,255,255,0.2)', flexShrink: 0 }} />

        {/* Design identity block */}
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2, flexShrink: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 500, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.07em', lineHeight: 1 }}>
            {formCfg.form_title ? `Form: ${formCfg.form_title}` : 'Form Design'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <i className="bi bi-palette-fill" style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }} />
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Design name"
              style={{
                background: 'transparent', border: 'none', borderBottom: '1.5px solid rgba(255,255,255,0.3)',
                color: '#fff', padding: '2px 0', fontSize: 15, fontWeight: 700, outline: 'none',
                width: Math.max(120, Math.min(name.length * 9 + 16, 280)),
              }} />
          </div>
        </div>

        <div style={{ flex: 1 }} />
        <button type="button" onClick={() => setTokens({ ...DEFAULT_TOKENS, ...design.tokens })}
          style={{ background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 8, color: 'rgba(255,255,255,0.85)', padding: '6px 14px', fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>
          <i className="bi bi-arrow-counterclockwise me-1" />Reset
        </button>
        <button type="button" onClick={() => onSave({ ...design, designName: name, tokens, isCustom: true })}
          style={{ background: '#fff', border: 'none', borderRadius: 8, color: theme.primary, padding: '6px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
          <i className="bi bi-check-lg me-1" />Save Design
        </button>
      </div>

      {/* ── Body ─────────────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Live preview panel */}
        <div style={{ width: '40%', flexShrink: 0, background: formCfg.bg_color || '#e8edf4', borderRight: '1px solid #d1d9e4', overflowY: 'auto', padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              <i className="bi bi-eye me-1" />Live Preview
            </div>
            <div style={{ fontSize: 10, color: '#94a3b8', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 4, padding: '2px 8px', fontWeight: 500 }}>
              {name || 'Untitled Design'}
            </div>
          </div>

          {/* Progress bar — only shown when staged form */}
          {showProgress && (
            <div style={{
              background: t.progressBgColor, borderRadius: t.progressBorderRadius, padding: t.progressPadding / 2,
              marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8,
            }}>
              {(stages.length ? stages : [{ stageLabel: 'Step 1' }, { stageLabel: 'Step 2' }]).slice(0, 4).map((s, i) => (
                <React.Fragment key={i}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <div style={{
                      width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: i === 0 ? t.submitBgColor : '#e2e8f0',
                      color: i === 0 ? t.submitTextColor : '#94a3b8',
                      fontSize: t.progressFontSize, fontWeight: t.progressFontWeight,
                    }}>{i + 1}</div>
                    <div style={{ fontSize: 9, color: i === 0 ? t.submitBgColor : '#94a3b8', fontWeight: i === 0 ? 600 : 400, whiteSpace: 'nowrap', maxWidth: 48, overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.stageLabel}</div>
                  </div>
                  {i < (stages.length || 2) - 1 && <div style={{ flex: 1, height: 2, background: '#e2e8f0', borderRadius: 2 }} />}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Form card — cfg overrides take precedence over design tokens, matching client renderer */}
          <div style={{
            background: (() => {
              const bgColor = formCfg.form_bg_color || t.formBgColor || '#ffffff';
              const op = formCfg.form_bg_opacity ?? t.formBgOpacity ?? 100;
              return op < 100 ? hexToRgba(bgColor, op / 100) : bgColor;
            })(),
            borderRadius: formCfg.form_border_radius ?? t.formBorderRadius,
            padding: `${formCfg.form_padding_y ?? t.formPaddingY}px ${formCfg.form_padding_x ?? t.formPaddingX}px`,
            border: (formCfg.form_border_visible ?? t.formBorderVisible)
              ? `${formCfg.form_border_width ?? t.formBorderWidth}px ${formCfg.form_border_style || t.formBorderStyle || 'solid'} ${formCfg.form_border_color || t.formBorderColor}`
              : '1px solid transparent',
            boxShadow: shadowMap[formCfg.form_shadow || t.formShadow] || 'none',
          }}>

            {/* Restore banner — only when persistence enabled */}
            {showBanner && (
              <div style={{
                background: t.restoreBannerBgColor, color: t.restoreBannerTextColor,
                border: `1px solid ${t.restoreBannerBorderColor}`, borderRadius: t.restoreBannerBorderRadius,
                padding: '7px 10px', fontSize: 11, marginBottom: t.fieldGap, display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <i className="bi bi-clock-history" />Restore your previous progress?
                <span style={{ marginLeft: 'auto', textDecoration: 'underline', cursor: 'pointer' }}>Restore</span>
              </div>
            )}

            {/* Form title — matches client placement inside the card */}
            {formCfg.form_title && (
              <div style={{ fontSize: 20, fontWeight: 700, color: formCfg.text_color || t.inputTextColor || '#1e293b', marginBottom: 16 }}>
                {formCfg.form_title}
              </div>
            )}

            {/* Fields — row grid / flat / placeholder, matching client rendering exactly */}
            {(() => {
              // Staged layout: show first stage's rows
              const stg = (formCfg.stages || []);
              if (stg.length > 0 && (stg[0]?.rows || []).length > 0) return renderPreviewRowGrid(stg[0].rows);
              // Row layout
              const rows = formCfg.rows || [];
              if (rows.length > 0) return renderPreviewRowGrid(rows);
              // Flat layout
              const fields = formCfg.fields || [];
              if (fields.length > 0) return (
                <div>
                  {fields.map(f => (
                    <div key={f.id} style={{ marginBottom: t.fieldGap }}>
                      <PreviewField field={f} />
                    </div>
                  ))}
                </div>
              );
              // Placeholder when no fields configured yet
              return (
                <div>
                  <div style={{ marginBottom: t.fieldGap }}><PreviewField field={{ label: 'Email address', type: 'email', placeholder: 'your@email.com', required: true }} /></div>
                  <div style={{ marginBottom: t.fieldGap }}><PreviewField field={{ label: 'Full name', type: 'text', placeholder: 'Jane Doe', required: true }} /></div>
                </div>
              );
            })()}

            {/* Buttons */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {isMultiStep && (
                <button style={{
                  background: t.navButtonStyle === 'ghost' ? 'transparent' : t.navButtonBgColor,
                  color: t.navButtonTextColor, border: t.navButtonStyle === 'outlined' ? `1px solid ${t.navButtonBgColor}` : 'none',
                  borderRadius: t.navButtonBorderRadius, padding: '7px 16px', fontSize: 13, cursor: 'pointer',
                }}>← {stages[0]?.backLabel || 'Back'}</button>
              )}
              <button style={submitStyle}>{submitLabel}</button>
            </div>
          </div>

          {/* Summary card — only shown for staged forms */}
          {showProgress && previewFields.length > 0 && (
            <div style={{ marginTop: 12, padding: '12px 14px', background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: t.summarySectionLabelColor, textTransform: 'uppercase', marginBottom: 6 }}>{stages[0]?.stageLabel || 'Stage 1'} Summary</div>
              {previewFields.slice(0, 2).map(f => (
                <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12 }}>
                  <span style={{ color: t.summarySectionLabelColor }}>{f.label}</span>
                  <span style={{ color: t.summaryValueColor }}>example value</span>
                </div>
              ))}
              <div style={{ marginTop: 6 }}>
                <span style={{ fontSize: 12, color: t.summaryEditButtonColor, cursor: 'pointer', fontWeight: 600 }}>✏ Edit</span>
              </div>
            </div>
          )}
        </div>

        {/* Controls panel */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Group tab bar */}
          <div style={{ display: 'flex', gap: 2, padding: '10px 20px', background: '#fff', borderBottom: '1px solid #e2e8f0', overflowX: 'auto', flexShrink: 0 }}>
            {TOKEN_GROUPS.map(g => (
              <button key={g.id} type="button" onClick={() => setGroup(g.id)} style={{
                display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
                padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13,
                background: group === g.id ? theme.primary : '#f1f5f9',
                color: group === g.id ? '#fff' : '#374151',
                fontWeight: group === g.id ? 600 : 400,
                flexShrink: 0,
              }}>
                <i className={`bi ${g.icon}`} />
                {g.label}
              </button>
            ))}
          </div>

          {/* Token rows — wrapped in context so stable module-level helpers can access tokens/cfg */}
          <TokenEditorCtx.Provider value={{ tokens, set, formCfg, handleCfgChange }}>
          <div style={{ flex: 1, overflowY: 'auto', padding: '4px 28px 24px' }}>

            {/* ── FIELDS ───────────────────────────────────────────────────── */}
            {group === 'fields' && <>
              <SecHead label="Colors" hint="What the input boxes look like when empty and when active" />
              <TRow label="Input background" hint="Fill color of empty input boxes"><ColTok k="inputBgColor" /></TRow>
              <TRow label="Background when focused" hint="Fill color while user is typing"><ColTok k="inputBgColorFocus" /></TRow>
              <TRow label="Text color" hint="Color of what the user types"><ColTok k="inputTextColor" /></TRow>
              <TRow label="Placeholder color" hint="Greyed-out hint text inside the input"><ColTok k="inputPlaceholderColor" /></TRow>

              <SecHead label="Border" hint="The outline drawn around each input box" />
              <TRow label="Border color"><ColTok k="inputBorderColor" /></TRow>
              <TRow label="Border color when focused" hint="Outline color when the user clicks into the field"><ColTok k="inputBorderColorFocus" /></TRow>
              <TRow label="Border color on error" hint="Outline color when a field has a validation error"><ColTok k="inputBorderColorError" /></TRow>
              <PairRow label="Border thickness & style">
                <NumTok k="inputBorderWidth" label="Width" max={8} suffix="px" />
                <SelTok k="inputBorderStyle" options={[{value:'solid',label:'Solid —'},{value:'dashed',label:'Dashed - -'},{value:'dotted',label:'Dotted ···'},{value:'none',label:'None'}]} />
              </PairRow>
              <TRow label="Corner rounding" hint="How rounded the corners of each input are">
                <RadiusPicker value={tokens.inputBorderRadius} onChange={v => set({ inputBorderRadius: v })} />
              </TRow>

              <SecHead label="Size & Spacing" hint="Input box dimensions and internal whitespace" />
              <TRow label="Input height" hint="Height of a single-line input"><NumTok k="inputHeight" min={24} max={72} suffix="px" /></TRow>
              <PairRow label="Internal padding" hint="Space between the text and the input border">
                <NumTok k="inputPaddingX" label="Left / Right" max={40} suffix="px" />
                <NumTok k="inputPaddingY" label="Top / Bottom" max={24} suffix="px" />
              </PairRow>
              <TRow label="Font size" hint="Size of text inside the input"><NumTok k="inputFontSize" min={10} max={22} suffix="px" /></TRow>
              <TRow label="Font weight"><SelTok k="inputFontWeight" options={WtOpts} /></TRow>

              <SecHead label="Focus Effect" hint="Visual feedback when a user clicks into a field" />
              <TRow label="Focus style">
                <VisualSeg value={tokens.inputShadowFocus} onChange={v => set({ inputShadowFocus: v })}
                  options={[
                    { value: 'none', label: 'None', icon: 'bi-slash-circle' },
                    { value: 'ring', label: 'Ring', icon: 'bi-record-circle' },
                    { value: 'glow', label: 'Glow', icon: 'bi-stars' },
                  ]} />
              </TRow>
              <TRow label="Focus ring color" hint="Color of the ring or glow around the focused field"><ColTok k="inputFocusRingColor" /></TRow>
              <TRow label="Focus ring width"><NumTok k="inputFocusRingWidth" max={8} suffix="px" /></TRow>
              <TRow label="Input shadow" hint="Subtle shadow on input boxes (separate from focus)">
                <ShadowPicker value={tokens.inputShadow} onChange={v => set({ inputShadow: v })} />
              </TRow>
              <TRow label="Animation speed" hint="How fast the focus color transition plays"><NumTok k="inputTransitionDuration" max={500} suffix="ms" /></TRow>
            </>}

            {/* ── TEXT & LABELS ────────────────────────────────────────────── */}
            {group === 'text' && <>
              <SecHead label="Labels" hint="Text shown above or beside each field" />
              <TRow label="Color"><ColTok k="labelColor" /></TRow>
              <TRow label="Font size"><NumTok k="labelFontSize" min={9} max={22} suffix="px" /></TRow>
              <TRow label="Font weight"><SelTok k="labelFontWeight" options={WtOpts} /></TRow>
              <TRow label="Font family"><SelTok k="labelFontFamily" options={FontFamilyOpts} /></TRow>
              <TRow label="Font style"><SelTok k="labelFontStyle" options={FontStyleOpts} /></TRow>
              <TRow label="Text alignment">
                <VisualSeg value={tokens.labelTextAlign || 'left'} onChange={v => set({ labelTextAlign: v })}
                  options={[
                    { value: 'left',   label: 'Left',   icon: 'bi-text-left' },
                    { value: 'center', label: 'Center', icon: 'bi-text-center' },
                    { value: 'right',  label: 'Right',  icon: 'bi-text-right' },
                  ]} />
              </TRow>
              <TRow label="Space below label" hint="Gap between the label and input box"><NumTok k="labelMarginBottom" max={20} suffix="px" /></TRow>
              <TRow label="Required field marker color" hint="Color of the asterisk (*) shown next to required fields"><ColTok k="requiredMarkColor" /></TRow>

              <SecHead label="Label Position" hint="Where labels appear relative to their input" />
              <TRow label="Position">
                <VisualSeg value={tokens.labelPosition} onChange={v => set({ labelPosition: v })}
                  options={[
                    { value: 'top',      label: 'Above',    icon: 'bi-align-top' },
                    { value: 'floating', label: 'Floating', icon: 'bi-magic' },
                    { value: 'left',     label: 'Left',     icon: 'bi-align-start' },
                  ]} />
              </TRow>

              <SecHead label="Input Text" hint="Text the user types into each field" />
              <TRow label="Text color"><ColTok k="inputTextColor" /></TRow>
              <TRow label="Font size"><NumTok k="inputFontSize" min={10} max={22} suffix="px" /></TRow>
              <TRow label="Font weight"><SelTok k="inputFontWeight" options={WtOpts} /></TRow>
              <TRow label="Font family"><SelTok k="inputFontFamily" options={FontFamilyOpts} /></TRow>
              <TRow label="Font style"><SelTok k="inputFontStyle" options={FontStyleOpts} /></TRow>
              <TRow label="Text alignment">
                <VisualSeg value={tokens.inputTextAlign || 'left'} onChange={v => set({ inputTextAlign: v })}
                  options={[
                    { value: 'left',   label: 'Left',   icon: 'bi-text-left' },
                    { value: 'center', label: 'Center', icon: 'bi-text-center' },
                    { value: 'right',  label: 'Right',  icon: 'bi-text-right' },
                  ]} />
              </TRow>

              <SecHead label="Placeholder Text" hint="Greyed hint text inside empty input fields" />
              <TRow label="Color"><ColTok k="inputPlaceholderColor" /></TRow>
              <TRow label="Font size" hint="Leave at 0 to match the input text size"><NumTok k="inputPlaceholderFontSize" min={0} max={22} suffix="px" /></TRow>
              <TRow label="Font weight" hint="Leave at 0 to match the input text weight"><NumTok k="inputPlaceholderFontWeight" min={0} max={900} /></TRow>
              <TRow label="Font family"><SelTok k="inputPlaceholderFontFamily" options={FontFamilyOpts} /></TRow>
              <TRow label="Font style"><SelTok k="inputPlaceholderFontStyle" options={FontStyleOpts} /></TRow>
            </>}

            {/* ── BUTTONS ──────────────────────────────────────────────────── */}
            {group === 'buttons' && <>
              <SecHead label="Button Labels" hint="Text shown on action buttons" />
              <TRow label="Submit button text" hint="Label on the final submit button"><CfgText k="button_text" placeholder="Submit" /></TRow>
              {isMultiStep && <>
                <TRow label="'Next' button text" hint="Default label — individual stages can override this"><CfgText k="defaultNextLabel" placeholder="Next" /></TRow>
                <TRow label="'Back' button text" hint="Default label — individual stages can override this"><CfgText k="defaultBackLabel" placeholder="Back" /></TRow>
                <TRow label="'Skip' button text" hint="Default label — individual stages can override this"><CfgText k="defaultSkipLabel" placeholder="Skip" /></TRow>
              </>}

              <SecHead label="Submit Button" hint="The primary action button that sends the form" />
              <TRow label="Background color"><ColTok k="submitBgColor" /></TRow>
              <TRow label="Background color on hover" hint="Color when the mouse is over the button"><ColTok k="submitBgColorHover" /></TRow>
              <TRow label="Text color"><ColTok k="submitTextColor" /></TRow>
              <TRow label="Corner rounding">
                <RadiusPicker value={tokens.submitBorderRadius} onChange={v => set({ submitBorderRadius: v })} />
              </TRow>
              <TRow label="Shadow">
                <ShadowPicker value={tokens.submitShadow} onChange={v => set({ submitShadow: v })} />
              </TRow>
              <PairRow label="Padding">
                <NumTok k="submitPaddingX" label="Left / Right" max={64} suffix="px" />
                <NumTok k="submitPaddingY" label="Top / Bottom" max={32} suffix="px" />
              </PairRow>
              <TRow label="Font size"><NumTok k="submitFontSize" min={10} max={22} suffix="px" /></TRow>
              <TRow label="Font weight"><SelTok k="submitFontWeight" options={WtOpts} /></TRow>
              <TRow label="Span full width" hint="Stretch the submit button across the full form width"><BoolTok k="submitFullWidth" /></TRow>

              {isMultiStep && <>
                <SecHead label="Navigation Buttons" hint="Back / Next / Skip buttons in multi-step forms" />
                <TRow label="Button style">
                  <VisualSeg value={tokens.navButtonStyle} onChange={v => set({ navButtonStyle: v })}
                    options={[
                      { value: 'filled',   label: 'Filled',   icon: 'bi-square-fill' },
                      { value: 'outlined', label: 'Outlined', icon: 'bi-square' },
                      { value: 'ghost',    label: 'Ghost',    icon: 'bi-dash-square' },
                    ]} />
                </TRow>
                <TRow label="Background color"><ColTok k="navButtonBgColor" /></TRow>
                <TRow label="Background color on hover"><ColTok k="navButtonHoverBgColor" /></TRow>
                <TRow label="Text color"><ColTok k="navButtonTextColor" /></TRow>
                <TRow label="Corner rounding">
                  <RadiusPicker value={tokens.navButtonBorderRadius} onChange={v => set({ navButtonBorderRadius: v })} />
                </TRow>
                <PairRow label="Padding">
                  <NumTok k="navButtonPaddingX" label="Left / Right" max={48} suffix="px" />
                  <NumTok k="navButtonPaddingY" label="Top / Bottom" max={24} suffix="px" />
                </PairRow>
                <TRow label="Font size"><NumTok k="navButtonFontSize" min={10} max={20} suffix="px" /></TRow>
                <TRow label="Font weight"><SelTok k="navButtonFontWeight" options={WtOpts} /></TRow>

                <SecHead label="Cancel Button" hint="Optional button that lets users exit or restart the form" />
                <TRow label="Button style">
                  <VisualSeg value={tokens.cancelButtonStyle} onChange={v => set({ cancelButtonStyle: v })}
                    options={[
                      { value: 'ghost',    label: 'Ghost',    icon: 'bi-dash-square' },
                      { value: 'outlined', label: 'Outlined', icon: 'bi-square' },
                      { value: 'filled',   label: 'Filled',   icon: 'bi-square-fill' },
                      { value: 'link',     label: 'Link',     icon: 'bi-link-45deg' },
                    ]} />
                </TRow>
                <TRow label="Background color" hint="Used when style is 'Filled'"><ColTok k="cancelButtonBgColor" /></TRow>
                <TRow label="Text color"><ColTok k="cancelButtonTextColor" /></TRow>
                <TRow label="Border color" hint="Visible for Ghost and Outlined styles"><ColTok k="cancelButtonBorderColor" /></TRow>
                <TRow label="Corner rounding">
                  <RadiusPicker value={tokens.cancelButtonBorderRadius} onChange={v => set({ cancelButtonBorderRadius: v })} />
                </TRow>
                <PairRow label="Padding">
                  <NumTok k="cancelButtonPaddingX" label="Left / Right" max={48} suffix="px" />
                  <NumTok k="cancelButtonPaddingY" label="Top / Bottom" max={24} suffix="px" />
                </PairRow>
                <TRow label="Font size"><NumTok k="cancelButtonFontSize" min={10} max={20} suffix="px" /></TRow>
              </>}
              {!isMultiStep && (
                <div style={{ padding: '12px 0', color: '#94a3b8', fontSize: 12, fontStyle: 'italic' }}>
                  Navigation and cancel buttons only appear in multi-step forms. Enable "Multi-step stages" in Fields & Rows to configure them.
                </div>
              )}
            </>}

            {/* ── VALIDATION ───────────────────────────────────────────────── */}
            {group === 'validation' && <>
              <SecHead label="Help Text" hint="Small descriptive text shown below each field" />
              <TRow label="Font size"><NumTok k="helpTextFontSize" min={9} max={16} suffix="px" /></TRow>
              <TRow label="Text color"><ColTok k="helpTextColor" /></TRow>

              <SecHead label="Error Messages" hint="Shown when a required field is empty or a value is invalid" />
              <TRow label="Error text color"><ColTok k="errorColor" /></TRow>
              <TRow label="Font size"><NumTok k="errorFontSize" min={9} max={16} suffix="px" /></TRow>
              <TRow label="Show warning icon" hint="Small icon shown to the left of the error message"><BoolTok k="errorIconVisible" /></TRow>

              <SecHead label="Success State" hint="Shown after the form is successfully submitted" />
              <TRow label="Success color"><ColTok k="successColor" /></TRow>
              <TRow label="Show success icon"><BoolTok k="successIconVisible" /></TRow>
            </>}

            {/* ── CARD & LAYOUT ─────────────────────────────────────────────── */}
            {group === 'layout' && <>
                  <SecHead label="Form Card" hint="The white box that wraps all form fields" />
                  <TRow label="Background color">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="color" value={formCfg.form_bg_color || '#ffffff'} onChange={e => handleCfgChange({ form_bg_color: e.target.value })}
                        style={{ width: 34, height: 30, padding: 2, border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer' }} />
                      <input type="text" value={formCfg.form_bg_color || ''} placeholder="From design"
                        onChange={e => handleCfgChange({ form_bg_color: e.target.value })}
                        style={{ width: 88, fontSize: 12, padding: '4px 7px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fafafa', fontFamily: 'monospace' }} />
                      {formCfg.form_bg_color && (
                        <button type="button" onClick={() => handleCfgChange({ form_bg_color: '' })}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 14, padding: 0 }}>
                          <i className="bi bi-x" />
                        </button>
                      )}
                    </div>
                  </TRow>
                  <TRow label="Background opacity" hint="Reduce to make the card semi-transparent">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="range" min={0} max={100} value={formCfg.form_bg_opacity ?? 100}
                        onChange={e => handleCfgChange({ form_bg_opacity: Number(e.target.value) })} style={{ width: 90 }} />
                      <span style={{ fontSize: 12, color: '#64748b', minWidth: 32 }}>{formCfg.form_bg_opacity ?? 100}%</span>
                    </div>
                  </TRow>
                  <TRow label="Corner rounding">
                    <RadiusPicker value={formCfg.form_border_radius ?? 12} onChange={v => handleCfgChange({ form_border_radius: v })} />
                  </TRow>
                  <TRow label="Shadow">
                    <ShadowPicker value={formCfg.form_shadow || ''} onChange={v => handleCfgChange({ form_shadow: v })} allowFrom />
                  </TRow>
                  <TRow label="Show border" hint="Draw an outline around the form card">
                    <Toggle value={!!formCfg.form_border_visible} onChange={v => handleCfgChange({ form_border_visible: v })} />
                  </TRow>
                  {formCfg.form_border_visible && <>
                    <TRow label="Border color">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input type="color" value={formCfg.form_border_color || '#e2e8f0'} onChange={e => handleCfgChange({ form_border_color: e.target.value })}
                          style={{ width: 34, height: 30, padding: 2, border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer' }} />
                      </div>
                    </TRow>
                    <PairRow label="Border thickness & style">
                      <CfgNum cfgKey="form_border_width" min={0} max={8} placeholder="1" />
                      <CfgSel cfgKey="form_border_style" dflt="solid" opts={[{value:'solid',label:'Solid —'},{value:'dashed',label:'Dashed - -'},{value:'dotted',label:'Dotted ···'}]} />
                    </PairRow>
                  </>}
                  <PairRow label="Inner padding" hint="Space between the form card edge and the fields inside">
                    <CfgNum cfgKey="form_padding_x" min={0} max={80} placeholder="From design" />
                    <span style={{ fontSize: 11, color: '#94a3b8', alignSelf: 'center' }}>Left/Right</span>
                    <CfgNum cfgKey="form_padding_y" min={0} max={80} placeholder="From design" />
                    <span style={{ fontSize: 11, color: '#94a3b8', alignSelf: 'center' }}>Top/Bottom</span>
                  </PairRow>
                  <TRow label="Form max-width" hint="Limits how wide the form card grows on wide screens">
                    <CfgNum cfgKey="form_max_width" min={240} max={1200} placeholder="From design" />
                  </TRow>

                  <SecHead label="Page / Section Background" hint="The area around the form card" />
                  <TRow label="Background color">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="color" value={formCfg.bg_color || '#ffffff'} onChange={e => handleCfgChange({ bg_color: e.target.value })}
                        style={{ width: 34, height: 30, padding: 2, border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer' }} />
                      <input type="text" value={formCfg.bg_color || ''} placeholder="Transparent"
                        onChange={e => handleCfgChange({ bg_color: e.target.value })}
                        style={{ width: 88, fontSize: 12, padding: '4px 7px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fafafa', fontFamily: 'monospace' }} />
                      {formCfg.bg_color && (
                        <button type="button" onClick={() => handleCfgChange({ bg_color: '' })}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 14, padding: 0 }}>
                          <i className="bi bi-x" />
                        </button>
                      )}
                    </div>
                  </TRow>
                  <TRow label="Text color" hint="Default text color for content in this section">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="color" value={formCfg.text_color || '#1e293b'} onChange={e => handleCfgChange({ text_color: e.target.value })}
                        style={{ width: 34, height: 30, padding: 2, border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer' }} />
                      <input type="text" value={formCfg.text_color || ''} placeholder="Default"
                        onChange={e => handleCfgChange({ text_color: e.target.value })}
                        style={{ width: 88, fontSize: 12, padding: '4px 7px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fafafa', fontFamily: 'monospace' }} />
                      {formCfg.text_color && (
                        <button type="button" onClick={() => handleCfgChange({ text_color: '' })}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 14, padding: 0 }}>
                          <i className="bi bi-x" />
                        </button>
                      )}
                    </div>
                  </TRow>
                  <TRow label="Section padding" hint="Space between the page edge and the form card">
                    <CfgSel cfgKey="padding" dflt="md" opts={[
                      {value:'none',label:'None'},
                      {value:'sm',label:'Small — 8px'},
                      {value:'md',label:'Medium — 16px'},
                      {value:'lg',label:'Large — 32px'},
                      {value:'xl',label:'X-Large — 48px'},
                      {value:'custom',label:'Custom…'},
                    ]} />
                  </TRow>
                  {formCfg.padding === 'custom' && (
                    <PairRow label="Custom padding">
                      <CfgNum cfgKey="section_padding_y" min={0} max={200} placeholder="0" />
                      <span style={{ fontSize: 11, color: '#94a3b8', alignSelf: 'center' }}>Top/Bottom</span>
                      <CfgNum cfgKey="section_padding_x" min={0} max={200} placeholder="0" />
                      <span style={{ fontSize: 11, color: '#94a3b8', alignSelf: 'center' }}>Left/Right</span>
                    </PairRow>
                  )}
                  <TRow label="Section width" hint="How wide the overall section spans">
                    <CfgSel cfgKey="form_width" dflt="normal" opts={[
                      {value:'narrow',label:'Narrow — 400px'},
                      {value:'normal',label:'Normal — 600px'},
                      {value:'wide',label:'Wide — 800px'},
                      {value:'full',label:'Full width'},
                      {value:'custom',label:'Custom…'},
                    ]} />
                  </TRow>
                  {formCfg.form_width === 'custom' && (
                    <TRow label="Custom max width"><CfgNum cfgKey="section_max_width" min={100} max={2560} placeholder="800" /></TRow>
                  )}

                  <SecHead label="Spacing" hint="Gaps between form fields" />
                  <TRow label="Space between fields" hint="Vertical gap between each field in a single-column layout">
                    <CfgNum cfgKey="field_gap" min={0} max={64} placeholder="From design" />
                  </TRow>
                  <TRow label="Space between rows" hint="Vertical gap between grid rows in a multi-column layout">
                    <CfgNum cfgKey="row_gap" min={0} max={64} placeholder="From design" />
                  </TRow>

                  <SecHead label="Form Design Tokens — Card" hint="Fine-tune the form card appearance in the design system" />
                  <TRow label="Card background color" hint="Default from design — override above takes precedence"><ColTok k="formBgColor" /></TRow>
                  <TRow label="Card corner rounding">
                    <RadiusPicker value={tokens.formBorderRadius} onChange={v => set({ formBorderRadius: v })} />
                  </TRow>
                  <TRow label="Card shadow">
                    <ShadowPicker value={tokens.formShadow} onChange={v => set({ formShadow: v })} />
                  </TRow>
                  <TRow label="Show card border"><BoolTok k="formBorderVisible" /></TRow>
                  {tokens.formBorderVisible && <>
                    <TRow label="Card border color"><ColTok k="formBorderColor" /></TRow>
                    <PairRow label="Border thickness & style">
                      <NumTok k="formBorderWidth" label="Width" max={8} suffix="px" />
                      <SelTok k="formBorderStyle" options={[{value:'solid',label:'Solid'},{value:'dashed',label:'Dashed'},{value:'dotted',label:'Dotted'}]} />
                    </PairRow>
                  </>}
                  <PairRow label="Card inner padding">
                    <NumTok k="formPaddingX" label="Left / Right" max={80} suffix="px" />
                    <NumTok k="formPaddingY" label="Top / Bottom" max={80} suffix="px" />
                  </PairRow>
                  <TRow label="Card max-width"><NumTok k="formMaxWidth" min={240} max={1200} suffix="px" /></TRow>
                  <PairRow label="Gap between fields">
                    <NumTok k="fieldGap" label="Fields" max={64} suffix="px" />
                    <NumTok k="rowGap" label="Rows" max={64} suffix="px" />
                  </PairRow>
            </>}

            {/* ── MULTI-STEP ───────────────────────────────────────────────── */}
            {group === 'multistep' && <>
              {!isMultiStep && (
                <div style={{ margin: '16px 0', padding: '12px 14px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, color: '#64748b' }}>
                  <i className="bi bi-info-circle me-2" />
                  These settings only take effect when the form uses <strong>multi-step stages</strong>. Enable "Multi-step stages" in Fields & Rows.
                </div>
              )}

              <SecHead label="Step Progress Indicator" hint="The bar or step dots shown at the top of a multi-step form" />
              <TRow label="Background color" hint="Background of the progress indicator strip"><ColTok k="progressBgColor" /></TRow>
              <TRow label="Corner rounding"><NumTok k="progressBorderRadius" max={24} suffix="px" /></TRow>
              <TRow label="Padding" hint="Inner space around the step indicators"><NumTok k="progressPadding" max={40} suffix="px" /></TRow>
              <TRow label="Step label size"><NumTok k="progressFontSize" min={9} max={18} suffix="px" /></TRow>
              <TRow label="Step label weight"><SelTok k="progressFontWeight" options={[{value:400,label:'Regular'},{value:500,label:'Medium'},{value:600,label:'Semi-bold'}]} /></TRow>

              <SecHead label="Restore Progress Banner" hint="Shown when a returning user has a saved draft to resume" />
              <TRow label="Background color" hint="Usually a light tint of the accent color"><ColTok k="restoreBannerBgColor" /></TRow>
              <TRow label="Text color"><ColTok k="restoreBannerTextColor" /></TRow>
              <TRow label="Border color"><ColTok k="restoreBannerBorderColor" /></TRow>
              <TRow label="Corner rounding"><NumTok k="restoreBannerBorderRadius" max={24} suffix="px" /></TRow>

              <SecHead label="Stage Summary Card" hint="Collapsed preview of a completed stage shown above the current step" />
              <TRow label="Field name color" hint="Color of each field's label in the summary"><ColTok k="summarySectionLabelColor" /></TRow>
              <TRow label="Field value color" hint="Color of the user's answer in the summary"><ColTok k="summaryValueColor" /></TRow>
              <TRow label="Edit button color" hint="Color of the 'Edit' link that takes the user back to a stage"><ColTok k="summaryEditButtonColor" /></TRow>
            </>}

          </div>
          </TokenEditorCtx.Provider>
        </div>
      </div>
    </div>
  );
}

// ─── Main editor ──────────────────────────────────────────────────────────────

export default function FormBuilderEditor({ section, dispatch, appId, pageVisibility }) {
  const { theme } = useTheme();
  const cfg = section.section_config || {};
  const [tokenEditorDesign, setTokenEditorDesign] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null); // { title, body, onConfirm, warn? }

  const primaryHex = theme.primary || '#4e2e80';

  const updateCfg = patch => dispatch({
    type: 'UPDATE_SECTION_CONFIG',
    clientId: section._clientId,
    patch,
  });

  // ── Flat field helpers ──────────────────────────────────────────────────────
  const fields  = cfg.fields || [];
  const dragIdx = useRef(null);

  // Guard: if the form is already persisted (form_id set), prompt before removing a field.
  // The backend will archive fields that have submission data, so data is never lost,
  // but we still warn the admin so they're aware.
  const confirmIfHasForm = (action) => {
    if (!cfg.form_id) { action(); return; }
    setConfirmModal({
      title: 'Remove field?',
      body: 'This form already has submissions. If this field collected data, it will be archived and remain visible in the Submissions view — nothing will be permanently lost.',
      warn: true,
      confirmLabel: 'Remove field',
      onConfirm: action,
    });
  };

  const addFlatField    = () => updateCfg({ fields: [...fields, newField(`Field ${fields.length + 1}`)] });
  const removeFlatField = id  => confirmIfHasForm(() => updateCfg({ fields: fields.filter(f => f.id !== id) }));
  const updateFlatField = (id, patch) => updateCfg({ fields: fields.map(f => f.id === id ? { ...f, ...patch } : f) });

  const handleFlatDrop = dropI => {
    if (dragIdx.current === null || dragIdx.current === dropI) return;
    const list = [...fields];
    const [moved] = list.splice(dragIdx.current, 1);
    list.splice(dropI > dragIdx.current ? dropI - 1 : dropI, 0, moved);
    updateCfg({ fields: list.map((f, i) => ({ ...f, order: i })) });
    dragIdx.current = null;
  };

  // ── Row helpers ─────────────────────────────────────────────────────────────
  const rows = cfg.rows || [];
  const addRow    = () => updateCfg({ rows: [...rows, newRow(cfg.defaultFieldsPerRow || 2)] });
  const updateRow = (ri, patch) => updateCfg({ rows: rows.map((r, i) => i === ri ? { ...r, ...patch } : r) });
  const removeRow = ri => updateCfg({ rows: rows.filter((_, i) => i !== ri) });

  // ── Stage helpers ───────────────────────────────────────────────────────────
  const stages   = cfg.stages || [];
  const addStage  = () => updateCfg({ stages: [...stages, newStage(stages.length + 1)] });
  const updateStage = (si, patch) => updateCfg({ stages: stages.map((s, i) => i === si ? { ...s, ...patch } : s) });
  const removeStage = si => updateCfg({ stages: stages.filter((_, i) => i !== si) });
  const moveStage   = (si, dir) => {
    const ni = si + dir;
    if (ni < 0 || ni >= stages.length) return;
    const list = [...stages];
    [list[si], list[ni]] = [list[ni], list[si]];
    updateCfg({ stages: list.map((s, i) => ({ ...s, stageNumber: i + 1 })) });
  };

  // ── Design helpers ──────────────────────────────────────────────────────────
  const customDesigns   = cfg.designs || [];
  const activeDesignId  = cfg.activeDesignId || '';
  const activeDesign    = customDesigns.find(d => d.designId === activeDesignId);
  const activePreset    = PRESET_DESIGNS.find(d => d.designId === activeDesignId);
  const activeDesignInfo = activeDesign || activePreset;

  const saveDesign = design => {
    const existing = customDesigns.findIndex(d => d.designId === design.designId);
    const updated  = existing >= 0
      ? customDesigns.map((d, i) => i === existing ? design : d)
      : [...customDesigns, { ...design, isCustom: true }];
    updateCfg({ designs: updated, activeDesignId: design.designId });
    setTokenEditorDesign(null);
  };

  const deleteCustomDesign = designId => {
    updateCfg({
      designs: (cfg.designs || []).filter(d => d.designId !== designId),
      activeDesignId: activeDesignId === designId ? '' : activeDesignId,
    });
  };

  // ── Persistence key ─────────────────────────────────────────────────────────
  const autoKey = `form_${String(cfg.form_id || section._clientId || 'x').slice(-8)}`;

  const isStaged    = cfg.stagesEnabled;
  const isRowLayout = cfg.rowLayoutEnabled;
  const totalFields = isStaged
    ? stages.reduce((n, s) => n + (s.rows || []).reduce((r, row) => r + (row.fields || []).length, 0), 0)
    : isRowLayout ? rows.reduce((n, r) => n + (r.fields || []).length, 0) : fields.length;

  // ─── Tab navigation ──────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('fields');

  const TABS = [
    { key: 'fields',   label: 'Fields',   icon: 'bi-layout-three-columns' },
    { key: 'data',     label: 'Data',     icon: 'bi-database' },
    { key: 'design',   label: 'Design',   icon: 'bi-palette' },
    { key: 'behavior', label: 'Behavior', icon: 'bi-gear' },
    { key: 'states',   label: 'States',   icon: 'bi-check-circle' },
  ];

  // ─── Rendered ────────────────────────────────────────────────────────────
  return (
    <FormBuilderContext.Provider value={{ appId, fields: allFormFields(cfg) }}>
    <div>

      {/* ── Form title ─────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Form Title</label>
        <input className="form-control form-control-sm"
          value={cfg.form_title || ''}
          onChange={e => updateCfg({ form_title: e.target.value })}
          placeholder="e.g. Contact Us" />
      </div>

      {/* ── Tab bar ────────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', gap: 0, borderBottom: '2px solid #e2e8f0',
        marginBottom: 14, overflowX: 'auto', flexShrink: 0,
      }}>
        {TABS.map(tab => (
          <button key={tab.key} type="button" onClick={() => setActiveTab(tab.key)} style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '9px 14px',
            border: 'none',
            borderBottom: activeTab === tab.key ? `2px solid ${theme.primary}` : '2px solid transparent',
            marginBottom: -2,
            background: 'transparent',
            color: activeTab === tab.key ? theme.primary : '#64748b',
            fontWeight: activeTab === tab.key ? 700 : 400,
            fontSize: 11,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            transition: 'color 0.15s',
          }}>
            <i className={`bi ${tab.icon}`} style={{ fontSize: 12 }} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── TAB: Fields ────────────────────────────────────────────────────── */}
      {activeTab === 'fields' && (<>
        {/* Fields & Rows */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '10px 0 8px' }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>FIELDS & ROWS</span>
          <div style={{ flex: 1, height: 1, background: '#f1f5f9' }} />
          {totalFields > 0 && <span style={{ fontSize: 9, fontWeight: 600, color: '#64748b', background: '#f1f5f9', borderRadius: 4, padding: '1px 6px' }}>{totalFields}</span>}
        </div>
        <InlineRow label="Multi-step stages">
          <Toggle value={isStaged || false} onChange={v => updateCfg({ stagesEnabled: v })} />
        </InlineRow>
        {!isStaged && (
          <IRow label="Layout mode" hint="Row layout lets you place multiple fields side-by-side using a grid.">
            <Seg value={isRowLayout ? 'row' : 'flat'} onChange={v => updateCfg({ rowLayoutEnabled: v === 'row' })}
              options={[
                { value: 'flat', label: 'Single column', icon: 'bi-list' },
                { value: 'row',  label: 'Multi-column',  icon: 'bi-layout-three-columns' },
              ]} />
          </IRow>
        )}

        {isStaged && (
          <div style={{ padding: '8px 10px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0', fontSize: 11, color: '#166534', marginTop: 6 }}>
            <i className="bi bi-info-circle me-1" />
            Fields are managed inside each stage in the <strong>Stage Configuration</strong> section below.
          </div>
        )}

        {!isStaged && !isRowLayout && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '10px 0 6px' }}>
              <span style={{ fontSize: 11, color: '#64748b' }}>{fields.length} field{fields.length !== 1 ? 's' : ''}</span>
              <button type="button" onClick={addFlatField}
                style={{ background: primaryHex, color: '#fff', border: 'none', fontSize: 11, padding: '4px 12px', borderRadius: 5, cursor: 'pointer' }}>
                <i className="bi bi-plus me-1" />Add Field
              </button>
            </div>
            {fields.map((field, idx) => (
              <div key={field.id} draggable
                onDragStart={() => { dragIdx.current = idx; }}
                onDragOver={e => e.preventDefault()}
                onDrop={() => handleFlatDrop(idx)}>
                <FieldCard field={field} onUpdate={p => updateFlatField(field.id, p)} onRemove={() => removeFlatField(field.id)} />
              </div>
            ))}
            {fields.length === 0 && (
              <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 11, padding: '16px 0', border: '1px dashed #e2e8f0', borderRadius: 8, marginTop: 6 }}>
                <i className="bi bi-ui-checks" style={{ fontSize: 22, display: 'block', marginBottom: 6 }} />
                No fields yet — click "Add Field" or choose a template above
              </div>
            )}
          </>
        )}

        {!isStaged && isRowLayout && (
          <>
            <IRow label="Default columns for new rows" hint="Each new row will start with this many columns.">
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                {[1, 2, 3, 4].map(n => (
                  <button key={n} type="button" onClick={() => updateCfg({ defaultFieldsPerRow: n })} style={{
                    width: 36, height: 28, border: '1px solid #e2e8f0', borderRadius: 6,
                    background: (cfg.defaultFieldsPerRow || 2) === n ? '#1e293b' : '#fff',
                    color: (cfg.defaultFieldsPerRow || 2) === n ? '#fff' : '#374151',
                    fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2,
                  }}>
                    {n === 1 ? '▌' : n === 2 ? '▐▌' : n === 3 ? '▐▌▌' : '▐▌▌▌'}
                  </button>
                ))}
              </div>
            </IRow>
            {rows.map((row, ri) => (
              <RowCard key={row.rowId} row={row} rowIndex={ri}
                onUpdateRow={p => updateRow(ri, p)} onRemoveRow={() => removeRow(ri)}
                onConfirmRemoveField={confirmIfHasForm} />
            ))}
            <button type="button" onClick={addRow} style={{
              width: '100%', border: '1px dashed #94a3b8', borderRadius: 6,
              background: 'none', color: '#64748b', fontSize: 11, padding: '7px 0', cursor: 'pointer',
            }}>
              <i className="bi bi-plus me-1" />Add row
            </button>
          </>
        )}

        {/* Stage Configuration — only shown for multi-stage forms */}
        {isStaged && (<>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '10px 0 8px' }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>STAGE CONFIGURATION</span>
            <div style={{ flex: 1, height: 1, background: '#f1f5f9' }} />
            {stages.length > 0 && <span style={{ fontSize: 9, fontWeight: 600, color: '#64748b', background: '#f1f5f9', borderRadius: 4, padding: '1px 6px' }}>{stages.length}</span>}
          </div>
          {stages.map((stage, si) => (
            <StageCard key={stage.stageId} stage={stage} stageIndex={si} stageCount={stages.length}
              onUpdate={p => updateStage(si, p)}
              onRemove={() => removeStage(si)}
              onMoveUp={() => moveStage(si, -1)}
              onMoveDown={() => moveStage(si, 1)}
              onConfirmRemoveField={confirmIfHasForm} />
          ))}
          <button type="button" onClick={addStage} style={{
            width: '100%', border: '1px dashed #94a3b8', borderRadius: 6,
            background: 'none', color: '#64748b', fontSize: 11, padding: '7px 0', cursor: 'pointer',
          }}>
            <i className="bi bi-plus me-1" />Add stage
          </button>
        </>)}
      </>)}

      {/* ── TAB: Design ────────────────────────────────────────────────────── */}
      {activeTab === 'design' && (<>
        {/* Form Design */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '10px 0 8px' }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>FORM DESIGN</span>
          <div style={{ flex: 1, height: 1, background: '#f1f5f9' }} />
        </div>

        {/* Active design card */}
        <div style={{ marginBottom: 12 }}>
          {activeDesignInfo ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#f8f5ff', borderRadius: 9, border: `1.5px solid ${activeDesignInfo.accentColor || '#4e2e80'}30` }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: activeDesignInfo.accentColor || '#4e2e80', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="bi bi-palette-fill" style={{ color: '#fff', fontSize: 13 }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>{activeDesignInfo.designName}</div>
                <div style={{ fontSize: 10, color: '#64748b' }}>{activeDesignInfo.description || 'Custom design'}</div>
              </div>
              <button type="button"
                onClick={() => setTokenEditorDesign({ ...activeDesignInfo, isCustom: true })}
                style={{ flexShrink: 0, background: activeDesignInfo.accentColor || '#4e2e80', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                <i className="bi bi-pencil me-1" />Customize
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#f8fafc', borderRadius: 9, border: '1px solid #e2e8f0' }}>
              <i className="bi bi-palette" style={{ fontSize: 20, color: '#94a3b8' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>No design selected</div>
                <div style={{ fontSize: 10, color: '#94a3b8' }}>Create a custom design using the button below.</div>
              </div>
            </div>
          )}
        </div>

        {/* Custom designs */}
        {customDesigns.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 7 }}>Custom Designs</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
              {customDesigns.map(design => (
                <DesignCard key={design.designId}
                  design={design}
                  isActive={activeDesignId === design.designId}
                  onSelect={() => updateCfg({ activeDesignId: design.designId })}
                  onEdit={() => setTokenEditorDesign({ ...design })}
                  onDelete={() => setConfirmModal({
                    title: 'Delete design?',
                    body: `"${design.designName}" will be permanently deleted.`,
                    onConfirm: () => deleteCustomDesign(design.designId),
                  })} />
              ))}
            </div>
          </div>
        )}

        <button type="button"
          onClick={() => setTokenEditorDesign({
            designId: uid('design'), designName: 'My Design',
            isDefault: false, isCustom: true, accentColor: primaryHex,
            tokens: { ...DEFAULT_TOKENS, ...(activeDesignInfo?.tokens || {}) },
          })}
          style={{ width: '100%', border: `1px dashed ${primaryHex}80`, borderRadius: 7, background: 'none', color: primaryHex, fontSize: 11, fontWeight: 500, padding: '8px 0', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <i className="bi bi-plus-circle" />Create custom design
        </button>

        {/* Display Mode */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '10px 0 8px' }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>DISPLAY MODE</span>
          <div style={{ flex: 1, height: 1, background: '#f1f5f9' }} />
        </div>
        <IRow label="How to show the form">
          <Seg value={cfg.display_mode || 'inline'} onChange={v => updateCfg({ display_mode: v })}
            options={[
              { value: 'inline', label: 'Inline on page', icon: 'bi-file-earmark' },
              { value: 'popup',  label: 'Popup on click', icon: 'bi-box-arrow-up-right' },
            ]} />
        </IRow>
        {cfg.display_mode === 'popup' && (
          <>
            <IRow label="Trigger button text">
              <input className="form-control form-control-sm" style={{ fontSize: 11 }} value={cfg.trigger_button_text || 'Open Form'}
                onChange={e => updateCfg({ trigger_button_text: e.target.value })} />
            </IRow>
            <IRow label="Trigger button style">
              <Seg value={cfg.trigger_button_style || 'filled'} onChange={v => updateCfg({ trigger_button_style: v })}
                options={[{ value: 'filled', label: 'Filled' }, { value: 'outline', label: 'Outlined' }, { value: 'ghost', label: 'Ghost' }]} small />
            </IRow>
            <IRow label="Trigger button color">
              <ColorField value={cfg.trigger_button_color || primaryHex} onChange={v => updateCfg({ trigger_button_color: v })} />
            </IRow>
            <IRow label="Popup size">
              <select className="form-select form-select-sm" style={{ fontSize: 11 }} value={cfg.popup_width || 'md'}
                onChange={e => updateCfg({ popup_width: e.target.value })}>
                <option value="sm">Small (400px)</option>
                <option value="md">Medium (540px)</option>
                <option value="lg">Large (720px)</option>
                <option value="xl">Extra Large (900px)</option>
              </select>
            </IRow>
          </>
        )}
      </>)}

      {/* ── TAB: Behavior ──────────────────────────────────────────────────── */}
      {activeTab === 'behavior' && (<>
        {/* Persistence & Cancel — only shown for multi-stage forms */}
        {isStaged && (<>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '10px 0 8px' }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>PERSISTENCE & CANCEL</span>
            <div style={{ flex: 1, height: 1, background: '#f1f5f9' }} />
          </div>
          <InlineRow label="Save progress to browser">
            <Toggle value={cfg.persistenceEnabled || false} onChange={v => updateCfg({ persistenceEnabled: v })} />
          </InlineRow>
          {cfg.persistenceEnabled && (
            <>
              <IRow label="Storage key">
                <div style={{ display: 'flex', gap: 6 }}>
                  <input className="form-control form-control-sm" style={{ fontSize: 11 }}
                    value={cfg.persistenceKey || autoKey}
                    onChange={e => updateCfg({ persistenceKey: e.target.value })} placeholder={autoKey} />
                  <button type="button" onClick={() => updateCfg({ persistenceKey: autoKey })}
                    style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 4, padding: '2px 8px', fontSize: 10, cursor: 'pointer', flexShrink: 0 }}>Auto</button>
                </div>
              </IRow>
              <IRow label="Data expiry">
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="number" min={0} value={cfg.persistenceExpiry ?? 24}
                    onChange={e => updateCfg({ persistenceExpiry: Number(e.target.value) })}
                    style={{ width: 56, fontSize: 11, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 4 }} />
                  <span style={{ fontSize: 11, color: '#64748b' }}>hours (0 = never expires)</span>
                </div>
              </IRow>
              <InlineRow label="Restore on page load">
                <Toggle value={cfg.restoreOnLoad !== false} onChange={v => updateCfg({ restoreOnLoad: v })} />
              </InlineRow>
              {cfg.restoreOnLoad !== false && (
                <>
                  <InlineRow label="Show restore banner">
                    <Toggle value={cfg.restoreBannerVisible !== false} onChange={v => updateCfg({ restoreBannerVisible: v })} />
                  </InlineRow>
                  {cfg.restoreBannerVisible !== false && (
                    <>
                      <IRow label="Banner message">
                        <textarea className="form-control form-control-sm" rows={2} style={{ fontSize: 11 }}
                          value={cfg.restoreBannerText || "You have a saved draft. Continue where you left off?"}
                          onChange={e => updateCfg({ restoreBannerText: e.target.value })} />
                      </IRow>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <IRow label="Continue label">
                          <input className="form-control form-control-sm" style={{ fontSize: 11 }} value={cfg.restoreBannerContinueLabel || 'Continue'}
                            onChange={e => updateCfg({ restoreBannerContinueLabel: e.target.value })} />
                        </IRow>
                        <IRow label="Start over label">
                          <input className="form-control form-control-sm" style={{ fontSize: 11 }} value={cfg.restoreBannerStartOverLabel || 'Start over'}
                            onChange={e => updateCfg({ restoreBannerStartOverLabel: e.target.value })} />
                        </IRow>
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          )}

          <SDiv label="Cancel Button" />
          <InlineRow label="Show cancel button">
            <Toggle value={cfg.cancelButtonVisible || false} onChange={v => updateCfg({ cancelButtonVisible: v })} />
          </InlineRow>
          {cfg.cancelButtonVisible && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <IRow label="Label">
                  <input className="form-control form-control-sm" style={{ fontSize: 11 }} value={cfg.cancelLabel || 'Cancel'}
                    onChange={e => updateCfg({ cancelLabel: e.target.value })} />
                </IRow>
                <IRow label="Size">
                  <select className="form-select form-select-sm" style={{ fontSize: 11 }} value={cfg.cancelSize || 'md'} onChange={e => updateCfg({ cancelSize: e.target.value })}>
                    <option value="sm">Small</option>
                    <option value="md">Medium</option>
                    <option value="lg">Large</option>
                  </select>
                </IRow>
              </div>
              <IRow label="Button style">
                <Seg value={cfg.cancelStyle || 'ghost'} onChange={v => updateCfg({ cancelStyle: v })}
                  options={[{ value: 'filled', label: 'Filled' }, { value: 'outlined', label: 'Outlined' }, { value: 'ghost', label: 'Ghost' }, { value: 'link', label: 'Link' }]} small />
              </IRow>
              <IRow label="Position">
                <Seg value={cfg.cancelPosition || 'inline-with-nav'} onChange={v => updateCfg({ cancelPosition: v })}
                  options={[{ value: 'left', label: 'Left' }, { value: 'right', label: 'Right' }, { value: 'inline-with-nav', label: 'Inline' }]} small />
              </IRow>
              <IRow label="When clicked">
                <select className="form-select form-select-sm" style={{ fontSize: 11 }} value={cfg.cancelAction || 'clear-form'}
                  onChange={e => updateCfg({ cancelAction: e.target.value })}>
                  <option value="clear-form">Clear form</option>
                  <option value="clear-and-reset-stage">Clear and go back to stage 1</option>
                  <option value="redirect">Redirect to URL</option>
                  <option value="confirm-then-clear">Ask for confirmation first</option>
                </select>
              </IRow>
              {cfg.cancelAction === 'redirect' && (
                <IRow label="Redirect URL">
                  <input className="form-control form-control-sm" style={{ fontSize: 11 }} value={cfg.cancelRedirectUrl || ''}
                    onChange={e => updateCfg({ cancelRedirectUrl: e.target.value })} placeholder="https://..." />
                </IRow>
              )}
              {cfg.cancelAction === 'confirm-then-clear' && (
                <IRow label="Confirm message">
                  <textarea className="form-control form-control-sm" rows={2} style={{ fontSize: 11 }}
                    value={cfg.cancelConfirmMessage || 'Are you sure you want to cancel?'}
                    onChange={e => updateCfg({ cancelConfirmMessage: e.target.value })} />
                </IRow>
              )}
              <InlineRow label="Also clear saved progress on cancel">
                <Toggle value={cfg.clearPersistenceOnCancel || false} onChange={v => updateCfg({ clearPersistenceOnCancel: v })} />
              </InlineRow>
            </>
          )}
        </>)}

        {/* Progress Indicator — only shown for multi-stage forms */}
        {isStaged && (<>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '10px 0 8px' }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>PROGRESS INDICATOR</span>
            <div style={{ flex: 1, height: 1, background: '#f1f5f9' }} />
          </div>
          <IRow label="Style" hint="How the progress through stages is shown at the top of the form.">
            <Seg value={cfg.progressIndicatorStyle || 'steps'} onChange={v => updateCfg({ progressIndicatorStyle: v })}
              options={[
                { value: 'steps', label: 'Steps' }, { value: 'bar', label: 'Bar' },
                { value: 'dots', label: 'Dots' }, { value: 'numbers', label: '1·2·3' }, { value: 'none', label: 'None' },
              ]} small />
          </IRow>
          {cfg.progressIndicatorStyle !== 'none' && (
            <>
              <IRow label="Position">
                <Seg value={cfg.progressPosition || 'top'} onChange={v => updateCfg({ progressPosition: v })}
                  options={[{ value: 'top', label: 'Top' }, { value: 'bottom', label: 'Bottom' }]} small />
              </IRow>
              <InlineRow label="Show stage labels"><Toggle value={cfg.progressShowLabels !== false} onChange={v => updateCfg({ progressShowLabels: v })} /></InlineRow>
              <InlineRow label="Connector line between steps"><Toggle value={cfg.progressConnectorVisible !== false} onChange={v => updateCfg({ progressConnectorVisible: v })} /></InlineRow>
              <InlineRow label="Clicking a step jumps back to it"><Toggle value={cfg.progressClickable || false} onChange={v => updateCfg({ progressClickable: v })} /></InlineRow>
              <InlineRow label="Animated transitions"><Toggle value={cfg.progressAnimated !== false} onChange={v => updateCfg({ progressAnimated: v })} /></InlineRow>
              <SDiv label="Colors (leave empty to use design defaults)" />
              <IRow label="Active step color"><ColorField value={cfg.progressActiveColor || ''} onChange={v => updateCfg({ progressActiveColor: v })} allowEmpty /></IRow>
              <IRow label="Completed step color"><ColorField value={cfg.progressCompleteColor || ''} onChange={v => updateCfg({ progressCompleteColor: v })} allowEmpty /></IRow>
              <IRow label="Inactive step color"><ColorField value={cfg.progressInactiveColor || ''} onChange={v => updateCfg({ progressInactiveColor: v })} allowEmpty /></IRow>
            </>
          )}
        </>)}

        {/* Submission Behaviour — private pages only */}
        {pageVisibility === 'private' && (<>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '10px 0 8px' }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>SUBMISSION BEHAVIOUR</span>
            <div style={{ flex: 1, height: 1, background: '#f1f5f9' }} />
          </div>

          {/* How submissions are stored */}
          <SDiv label="User Submission Behaviour" />
          <IRow
            label="Submission mode"
            hint={
              'Controls how the form handles repeat submissions from the same user.\n\n' +
              '• Always new — every submit creates a fresh row (works for everyone, including anonymous visitors).\n' +
              '• One per user — authenticated users can only have one submitted record; re-opening the form pre-fills their existing answer.\n' +
              '• Draft & resume — auto-saves a draft after each stage; the user can return later and continue from where they left off. Requires login.\n' +
              '• Edit existing — pre-fills the form with the user\'s last submitted record for editing. Requires login.\n\n' +
              'Note: modes other than "Always new" require the user to be logged in. On public pages an unauthenticated submit always falls back to "Always new".'
            }
          >
            <select
              className="form-select form-select-sm"
              style={{ fontSize: 11 }}
              value={cfg.submission_behavior || 'always_new'}
              onChange={e => updateCfg({ submission_behavior: e.target.value })}
            >
              <option value="always_new">Always create a new submission</option>
              <option value="one_per_user">One submission per user (edit existing)</option>
              <option value="draft_resume">Auto-save draft; resume from last stage</option>
              <option value="edit_existing">Pre-fill with existing submission for editing</option>
            </select>
          </IRow>

          {/* Post-submit behaviour */}
          <SDiv label="After Submission" />
          <IRow
            label="Post-submit behaviour"
            hint={
              '• No editing — show success message; the form is done.\n' +
              '• Allow editing — show success message with an "Edit my submission" button so the user can come back and change their answer.\n' +
              '• Show completed — display a locked/completed status view; no further interaction.'
            }
          >
            <select
              className="form-select form-select-sm"
              style={{ fontSize: 11 }}
              value={cfg.post_submit_behavior || 'no_edit'}
              onChange={e => updateCfg({ post_submit_behavior: e.target.value })}
            >
              <option value="no_edit">Show success message — no editing</option>
              <option value="allow_edit">Allow user to edit after submission</option>
              <option value="show_completed">Show completed / locked status</option>
            </select>
          </IRow>

          {cfg.post_submit_behavior === 'show_completed' && (
            <IRow label="Completed message" hint="Text shown in the locked completed view.">
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ fontSize: 11 }}
                value={cfg.completed_message || ''}
                onChange={e => updateCfg({ completed_message: e.target.value })}
                placeholder="Submission complete."
              />
            </IRow>
          )}

          {/* Note about public forms */}
          <div style={{
            marginTop: 12, padding: '8px 10px',
            background: '#f0f9ff', borderRadius: 6, border: '1px solid #bae6fd',
            fontSize: 10, color: '#0369a1', lineHeight: 1.5,
          }}>
            <i className="bi bi-info-circle me-1" />
            <strong>Public form behavior:</strong> No separate authentication toggle is needed.
            When the form is on a <em>public page</em>, anonymous visitors always submit as "new".
            Logged-in users automatically have their submissions linked to their account and the
            selected submission mode applies. On a <em>private page</em> the user is always
            authenticated before the page loads, so all modes work automatically.
          </div>
        </>)}

        {/* Show a hint when no behavior options are visible */}
        {!isStaged && pageVisibility !== 'private' && (
          <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 11, padding: '24px 0' }}>
            <i className="bi bi-info-circle" style={{ fontSize: 18, display: 'block', marginBottom: 6, color: '#cbd5e1' }} />
            Behavior settings appear when multi-step stages are enabled or the page is private.
          </div>
        )}
      </>)}

      {/* ── TAB: Data ──────────────────────────────────────────────────────── */}
      {activeTab === 'data' && (
        <FormDataTab cfg={cfg} updateCfg={updateCfg} appId={appId} />
      )}

      {/* ── TAB: States ────────────────────────────────────────────────────── */}
      {activeTab === 'states' && (<>
        {/* Success & Error States */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '10px 0 8px' }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>SUCCESS & ERROR STATES</span>
          <div style={{ flex: 1, height: 1, background: '#f1f5f9' }} />
        </div>
        <IRow label="Success message" hint="Shown after the form is submitted successfully.">
          <input className="form-control form-control-sm" value={cfg.success_message || 'Thank you!'}
            onChange={e => updateCfg({ success_message: e.target.value })} />
        </IRow>

        {/* Where the person goes next. Without this a form stays put and shows
            its message, which is the behaviour every existing form has. */}
        <IRow label="After a successful submit" hint="Stay on the form, or send the person somewhere else.">
          <select
            className="form-select form-select-sm"
            style={{ fontSize: 11 }}
            value={(cfg.on_success || {}).mode || 'message'}
            onChange={e => updateCfg({
              on_success: { ...(cfg.on_success || {}), mode: e.target.value },
            })}
          >
            <option value="message">Show the message and stay here</option>
            <option value="navigate">Go to another page</option>
            <option value="message_then_navigate">Show the message, then go</option>
            <option value="message_then_button">Show the message with a button</option>
          </select>
        </IRow>

        {((cfg.on_success || {}).mode || 'message') !== 'message' && (
          <>
            <IRow
              label="Go to"
              hint="A path on this site, or a full URL. Use @ref to drop in something this form just created."
            >
              <input
                className="form-control form-control-sm"
                style={{ fontSize: 11 }}
                placeholder="/my-registrations  or  /events/@r1"
                value={(cfg.on_success || {}).url || ''}
                onChange={e => updateCfg({
                  on_success: { ...(cfg.on_success || {}), url: e.target.value },
                })}
              />
            </IRow>

            {(cfg.on_success || {}).mode === 'message_then_navigate' && (
              <IRow label="Wait first (ms)" hint="How long the success message is shown before leaving.">
                <input
                  type="number"
                  min={0}
                  className="form-control form-control-sm"
                  style={{ fontSize: 11 }}
                  value={(cfg.on_success || {}).delay_ms === undefined ? 1500 : cfg.on_success.delay_ms}
                  onChange={e => updateCfg({
                    on_success: {
                      ...(cfg.on_success || {}),
                      delay_ms: parseInt(e.target.value, 10) || 0,
                    },
                  })}
                />
              </IRow>
            )}

            {(cfg.on_success || {}).mode === 'message_then_button' && (
              <IRow label="Button label" hint="Shown under the success message. The person goes to “Go to” when they click it.">
                <input
                  className="form-control form-control-sm"
                  style={{ fontSize: 11 }}
                  placeholder="Continue"
                  value={(cfg.on_success || {}).button_label || ''}
                  onChange={e => updateCfg({
                    on_success: { ...(cfg.on_success || {}), button_label: e.target.value },
                  })}
                />
              </IRow>
            )}

            {!(cfg.on_success || {}).url && (
              <div style={{
                margin: '4px 0 8px', padding: '7px 9px', background: '#fffbeb',
                borderRadius: 6, border: '1px solid #fde68a', fontSize: 10,
                color: '#92400e', lineHeight: 1.5,
              }}>
                <i className="bi bi-exclamation-triangle me-1" />
                {(cfg.on_success || {}).mode === 'message_then_button'
                  ? 'No destination set, so no button will be shown.'
                  : 'No destination set, so the form will stay where it is.'}
              </div>
            )}

            <div style={{
              margin: '4px 0 8px', padding: '7px 9px', background: '#f0f9ff',
              borderRadius: 6, border: '1px solid #bae6fd', fontSize: 10,
              color: '#0369a1', lineHeight: 1.5,
            }}>
              <i className="bi bi-info-circle me-1" />
              Writing to a table? <code>@r1</code> becomes the id of the row the first table
              wrote — so <code>/events/@r1</code> sends the person to the record they just made.
            </div>
          </>
        )}
        {isStaged && (
          <>
            <SDiv label="Stage Summary Cards" />
            <InlineRow label="Show 'Edit' button in summaries">
              <Toggle value={cfg.summaryEditButtonVisible !== false} onChange={v => updateCfg({ summaryEditButtonVisible: v })} />
            </InlineRow>
            {cfg.summaryEditButtonVisible !== false && (
              <IRow label="Edit button label">
                <input className="form-control form-control-sm" style={{ fontSize: 11 }} value={cfg.summaryEditLabel || 'Edit'}
                  onChange={e => updateCfg({ summaryEditLabel: e.target.value })} />
              </IRow>
            )}
            <InlineRow label="Mask sensitive fields in summaries">
              <Toggle value={cfg.summaryMaskSensitiveFields !== false} onChange={v => updateCfg({ summaryMaskSensitiveFields: v })} />
            </InlineRow>
          </>
        )}
      </>)}

      {/* ── Design token editor (fullscreen overlay) ─────────────────────────── */}
      {tokenEditorDesign && (
        <DesignTokenEditor
          key={`${section._clientId}_${tokenEditorDesign.designId}`}
          design={tokenEditorDesign}
          cfg={cfg}
          onCfgChange={updateCfg}
          onSave={saveDesign}
          onBack={() => setTokenEditorDesign(null)}
        />
      )}

      {/* ── Confirm modal ──────────────────────────────────────────────────────── */}
      {confirmModal && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setConfirmModal(null)}
        >
          <div
            style={{
              background: '#fff', borderRadius: 12, padding: '24px 28px',
              maxWidth: 400, width: '92%',
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                background: confirmModal.warn ? '#fff7ed' : '#f0fdf4',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <i className={`bi ${confirmModal.warn ? 'bi-exclamation-triangle-fill' : 'bi-stars'}`}
                  style={{ color: confirmModal.warn ? '#f59e0b' : '#16a34a', fontSize: 18 }} />
              </div>
              <span style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>{confirmModal.title}</span>
            </div>
            <p style={{ fontSize: 13, color: '#475569', margin: '0 0 20px', lineHeight: 1.5 }}>
              {confirmModal.body}
            </p>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button"
                onClick={() => setConfirmModal(null)}
                style={{
                  padding: '7px 18px', borderRadius: 7, border: '1px solid #e2e8f0',
                  background: '#f8fafc', color: '#374151', fontSize: 13, cursor: 'pointer', fontWeight: 500,
                }}>
                Cancel
              </button>
              <button type="button"
                onClick={() => { confirmModal.onConfirm(); setConfirmModal(null); }}
                style={{
                  padding: '7px 18px', borderRadius: 7, border: 'none',
                  background: primaryHex, color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 600,
                }}>
                {confirmModal.confirmLabel || (confirmModal.warn ? 'Replace & Apply' : 'Confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
    </FormBuilderContext.Provider>
  );
}
